import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { IcUndo, IcSearch, IcScanBarcode, IcPlus, IcBanknote, IcUser, IcPackage, IcClose, IcTrash, IcShoppingCart, IcPercent, IcMinus, IcAlertTriangle, IcGift, IcAward, IcTrophy, IcCheckCircle, IcHistory, IcSparkles, IcLock, IcChevronLeft, IcChevronRight, IcFilters, IcClipboardList, IcCheck, IcCalendar, IcSun, IcMoon, IcBoxes, IcPin, IcPackageOpen, IcFlask } from '../../theme/icons';
import useIsMobile from '../../hooks/useIsMobile';
import salesService from '../../services/salesService';
import laboratoireService from '../../services/laboratoireService';
import reservationService from '../../services/reservationService';
import productService from '../../services/productService';
import { peekCached } from '../../services/apiCache';
import { lastKnownCatalog } from '../../offline/productCache';
import promoService from '../../services/promoService';
import customerService from '../../services/customerService';
import categoryService from '../../services/categoryService';
import subcategoryService from '../../services/subcategoryService';
import clientCategoryService from '../../services/clientCategoryService';
import rewardService from '../../services/rewardService';
import serviceManagementService from '../../services/serviceManagementService';
import { useCompany } from '../../context/CompanyContext';
import VenteTabs from '../../components/VenteTabs';
import Swal from '../../theme/swal';
import { normProductName, duplicateProductAlert } from '../../utils/duplicateProduct';
import { filterSearch, matchesSearch } from '../../utils/search';
import KI from '../../theme/palette';
import { BtnSk } from '../../components/Skeleton';
import { requestIdentity, withIdentity, actorFields, stampAs } from '../../services/identityGate';
import authService from '../../services/authService';

const SellerSalesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Pricing rates from company settings (Paramètres > Tarification) — used to
  // estimate a new product's selling price from its purchase price (cost × TVA × marge).
  const { marge: companyMarge, tva: companyTva } = useCompany();
  const TVA_RATE = (Number(companyTva) || 19) / 100;
  const DEFAULT_MARGIN_RATE = (Number(companyMarge) || 50) / 100;

  // Add custom animations
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes scaleIn {
      from { transform: scale(0.8); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
    @keyframes slideIn {
      from { transform: translateX(-20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    .animate-fadeIn {
      animation: fadeIn 0.3s ease-out;
    }
    .animate-scaleIn {
      animation: scaleIn 0.4s ease-out;
    }
    .animate-slideIn {
      animation: slideIn 0.5s ease-out forwards;
    }
  `;
  if (!document.querySelector('style[data-seller-animations]')) {
    style.setAttribute('data-seller-animations', 'true');
    document.head.appendChild(style);
  }

  // State for products and search
  const [products, setProducts] = useState([]);
  const [activePromos, setActivePromos] = useState([]); // POS promotions (single + pack)
  // Click a product photo to preview it (name, photo, description, labo, catégorie).
  const [previewProduct, setPreviewProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  // When this sale was opened from a reservation ("Vendre au client"), the
  // reservation id to mark "Vendue" once the sale is finalized.
  const [pendingReservationId, setPendingReservationId] = useState(null);
  // When this sale was opened from « Terminer » on a dashboard session, the
  // session id to mark « completed » once the sale is finalized — NOT before,
  // so an abandoned panier leaves the session open.
  const [pendingSessionId, setPendingSessionId] = useState(null);
  // Avance déjà encaissée sur la réservation en cours de vente (P12) : elle
  // compte comme paiement déjà reçu — déduite du reste à payer, jamais des points.
  const [reservationAvance, setReservationAvance] = useState(0);
  // Réservations « Bloquée » (unités physiquement mises de côté, déjà hors
  // stock) : servent à re-proposer le produit UNIQUEMENT quand le client
  // sélectionné est celui pour qui il est bloqué.
  const [blockedReservations, setBlockedReservations] = useState([]);

  // State for services
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');

  // Standalone catalog overlay with swipeable Produits/Services tabs
  const [showCatalog, setShowCatalog] = useState(true); // catalogue ouvert par défaut
  const [catalogTab, setCatalogTab] = useState('products'); // 'products' | 'services'
  const [dragDx, setDragDx] = useState(0);
  const swipeRef = React.useRef(null); // { x, y, axis }
  const openCatalog = (tab) => { setCatalogTab(tab); setShowCatalog(true); };

  const onCatalogTouchStart = (e) => {
    swipeRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, axis: null };
  };
  const onCatalogTouchMove = (e) => {
    const s = swipeRef.current;
    if (!s) return;
    const dx = e.touches[0].clientX - s.x;
    const dy = e.touches[0].clientY - s.y;
    if (!s.axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      s.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (s.axis !== 'x') return; // vertical → let the panel scroll
    let d = dx;
    // Rubber-band resistance at the edges
    if (catalogTab === 'products' && d > 0) d *= 0.25;
    if (catalogTab === 'services' && d < 0) d *= 0.25;
    setDragDx(d);
  };
  const onCatalogTouchEnd = () => {
    const s = swipeRef.current;
    const threshold = 60;
    if (s && s.axis === 'x') {
      if (dragDx <= -threshold && catalogTab === 'products') setCatalogTab('services');
      else if (dragDx >= threshold && catalogTab === 'services') setCatalogTab('products');
    }
    setDragDx(0);
    swipeRef.current = null;
  };
  // Sessions (rendez-vous) — used to decide which SOIN services are sellable
  const [sessions, setSessions] = useState([]);
  
  // State for category/subcategory filters
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [selectedSubcategoryFilter, setSelectedSubcategoryFilter] = useState('');
  // New vente-page layout (factures-style): filters panel, laboratoire filter, panier/client popups
  const [showFilters, setShowFilters] = useState(false);
  // Mobile : la recherche est repliée derrière une loupe (Ahmed 2026-08-16).
  const isMobileView = useIsMobile();
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');            // product search inside the popup
  const [orderSearchFocus, setOrderSearchFocus] = useState(false); // show the "Nouveau produit" dropdown on focus
  const [reserveSelected, setReserveSelected] = useState([]);    // products picked (multi-select)
  const [reserveStep, setReserveStep] = useState('select');      // 'select' | 'form'
  const [reserveForm, setReserveForm] = useState({ customer_name: '', customer_phone: '', expected_arrival_date: '', note: '' });
  // "Nom du client" autocomplete on the reservation form: pick an existing client
  // (captures their phone → the later sale can preselect them if fidélité member),
  // or type a free name (non-member → later sale stays "Client Passager").
  const [orderClientResults, setOrderClientResults] = useState([]);
  const [showOrderClients, setShowOrderClients] = useState(false);
  const [reserveLines, setReserveLines] = useState([]);          // [{ id, name, quantity, deposit_amount, isNew, estimated_cost, estimated_sale_price }]
  const [showNewProductForm, setShowNewProductForm] = useState(false); // "+ Nouveau produit" sub-popup
  const [newProduct, setNewProduct] = useState({ name: '', cost: '' });
  const newProductSeqRef = React.useRef(0);
  const newProductNameRef = React.useRef(null); // input nom (focus sur « Modifier le nom »)
  const [reserveSubmitting, setReserveSubmitting] = useState(false);
  // "Réserver un soin" popup — creates a session (rendez-vous) for a soin
  const [showSoinModal, setShowSoinModal] = useState(false);
  const [soinStep, setSoinStep] = useState('select');         // 'select' | 'confirm'
  const [soinForm, setSoinForm] = useState({ service: '', notes: '' });
  const [soinCustomer, setSoinCustomer] = useState(null);
  const [soinCustomerQuery, setSoinCustomerQuery] = useState('');
  const [soinCustomers, setSoinCustomers] = useState([]);
  const [showSoinCustomerDropdown, setShowSoinCustomerDropdown] = useState(false);
  const [soinCalMonth, setSoinCalMonth] = useState({ year: 2026, month: 0 }); // displayed month
  const [soinDay, setSoinDay] = useState('');                 // 'YYYY-MM-DD'
  const [soinPeriod, setSoinPeriod] = useState('');           // 'matin' | 'apresmidi'
  const [soinPayment, setSoinPayment] = useState('unpaid');   // 'paid' | 'unpaid'
  const [soinPayWithPoints, setSoinPayWithPoints] = useState(false); // pay the soin with points at booking (cost from category Avantages)
  const [soinSubmitting, setSoinSubmitting] = useState(false);
  const [labFilter, setLabFilter] = useState('');
  const [laboratoires, setLaboratoires] = useState([]);
  const [showPanier, setShowPanier] = useState(false);
  // Retour client : le bouton « Retour » de l'entête du panier fait de TOUT le
  // panier un retour (stock repris, client remboursé, points repris) au lieu
  // d'une vente. refundMode 'credit' = déduit d'abord de son crédit.
  const [returnMode, setReturnMode] = useState(false);
  const [refundMode, setRefundMode] = useState('cash');
  // Historique d'achat du client par produit du panier (mode retour, vrai client).
  const [returnHistory, setReturnHistory] = useState({});
  // Retour = manager ou propriétaire seulement : { who, at } de l'accord donné
  // par code sur le compte vendeur (il signe aussi le retour).
  const returnApproval = useRef(null);

  // Caméra en ARRIÈRE-PLAN dès le 1er article du panier (owner 2026-09-29) :
  // au moment d'encaisser, la personne est déjà reconnue — rien à attendre.
  // Panier vidé (vente faite, annulée) = on oublie ; quitter la page aussi.
  const [showClientPicker, setShowClientPicker] = useState(false);

  // State for customer selection
  const [customers, setCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState('');
  const [showCustomers, setShowCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerCategory, setCustomerCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [newCustomerData, setNewCustomerData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    points: '',
    credit: '',
    credit_maximum: '',
    score: '',
    category_id: ''
  });

  // State for discount and payment
  // Global remise is a PERCENT only (no DT mode). It acts as the default that
  // seeds every non-edited product line; manually edited lines keep their own %.
  const [discount, setDiscount] = useState(0);
  const [amountPaid, setAmountPaid] = useState(0);
  // Editing buffers (null = not editing): let the user CLEAR these number fields
  // (show empty) while typing, instead of forcing a 0. Reset to clean value onBlur.
  const [discountRaw, setDiscountRaw] = useState(null);
  const [recevoirRaw, setRecevoirRaw] = useState(null);
  const [amountPaidRaw, setAmountPaidRaw] = useState(null);
  // Loyalty points used as payment in the panier (1 pt = 1 DT), fidèle clients only.
  const [pointsToUse, setPointsToUse] = useState(0);
  const [pointsToUseRaw, setPointsToUseRaw] = useState(null);
  // Becomes true once the cashier edits "Montant payé" so the cart total no longer
  // auto-overwrites it. Lets a manual 0 (client can't pay) stick.
  const [amountPaidTouched, setAmountPaidTouched] = useState(false);

  // State for form submission
  const [submitting, setSubmitting] = useState(false);

  // State for rewards bubble and dialog
  const [showRewardsBubble, setShowRewardsBubble] = useState(false);
  const [rewardsSummary, setRewardsSummary] = useState(null);
  const [showRewardsDialog, setShowRewardsDialog] = useState(false);
  const [nextFeature, setNextFeature] = useState(null);
  const [showRewardEarnedModal, setShowRewardEarnedModal] = useState(false);
  const [earnedReward, setEarnedReward] = useState(null);
  const [earnedRewardCustomerId, setEarnedRewardCustomerId] = useState(null);
  const [useRewardDialogOpen, setUseRewardDialogOpen] = useState(false);
  const [rewardToUse, setRewardToUse] = useState(null);
  const [confirmRewardDialog, setConfirmRewardDialog] = useState(false);
  const [rewardToConfirm, setRewardToConfirm] = useState(null);

  // Check if selected customer is Passager (id=5)
  const isPassagerCustomer = () => {
    return selectedCustomer && selectedCustomer.id === 5;
  };

  // The facture / client remise (%) — now applied PER LINE in the panier as a
  // pre-filled "suggestion to confirm" (not just placeholder text). Passager
  // (walk-in, id 5) defaults to 5%; everyone else inherits their category remise.
  // It is applied per product line and NOT also globally, to avoid double-discount.
  const factureRemisePercent = selectedCustomer
    ? (selectedCustomer.id === 5 ? 5 : (parseFloat(customerCategory?.remise) || 0))
    : 0;

  // "Gratuit" (comp): an ACTE or DIAGNOSTIC service in the panier can be offered for
  // free with a toggle button — for ANY client (Passager, fidèle, fidèle gold). The
  // cashier can still charge it normally, or press the button to make that line 0 DT.
  const GRATUIT_SERVICE_TYPES = ['ACTE', 'DIAGNOSTIC'];
  const serviceCanBeGratuit = (service) =>
    GRATUIT_SERVICE_TYPES.includes(service.type);
  const isServiceGratuit = (service) => !!service.gratuit && serviceCanBeGratuit(service);

  // A soin's price IN POINTS is configured in "Gestion des Catégories Client" — each
  // category's "Avantages" (features) is a list of { points, feature } where the
  // feature label is the soin name and points is its cost. So paying a soin with
  // points does NOT follow 1 pt = 1 DT: it uses the category-defined points cost.
  const categoryById = (id) => allCategories.find(c => String(c.id) === String(id?.id ?? id)) || null;
  // Labels are typed by hand on both sides (service name vs Avantage), so the
  // match must survive case, accents and spacing differences — and containment,
  // because the Avantage "Hydraphacial" refers to the soin "Soin Hydraphacial".
  const normalizeLabel = (s) => (s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/\s+/g, ' ').trim();
  const soinPointsCost = (category, soin) => {
    if (!category || !soin || !Array.isArray(category.features)) return null;
    const name = normalizeLabel(soin.name);
    if (!name) return null;
    const feats = category.features.filter(ft => ft && typeof ft === 'object');
    let f = feats.find(ft => normalizeLabel(ft.feature) === name);
    if (!f) {
      f = feats
        .filter(ft => {
          const label = normalizeLabel(ft.feature);
          return label.length >= 4 && (name.includes(label) || label.includes(name));
        })
        .sort((a, b) => normalizeLabel(b.feature).length - normalizeLabel(a.feature).length)[0] || null;
    }
    return f ? (parseFloat(f.points) || 0) : null; // null = soin not configured for this category
  };

  // A SOIN line in the panier can be settled with loyalty points at the same
  // category-defined cost as booking-time payment (Avantages, not 1 pt = 1 DT).
  // Any fidèle client qualifies — the gold-only rule applies to PRODUCTS only.
  const servicePointsCost = (service) =>
    service?.type === 'SOIN' && selectedCustomer && !isPassagerCustomer()
      ? soinPointsCost(customerCategory, service)
      : null;
  const isServicePaidWithPoints = (service) =>
    !!service.payWithPoints && servicePointsCost(service) !== null;

  const loadBlockedReservations = async () => {
    try {
      const data = await reservationService.getReservations({ status: 'blocked' });
      setBlockedReservations(Array.isArray(data) ? data : (data?.results || []));
    } catch (err) {
      console.error('Error loading blocked reservations:', err);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadProducts();
    loadBlockedReservations();
    loadServices();
    loadSessions();
    // Auto-select Passager on initial load only — NOT when a client is deep-linked
    // (dashboard « Terminer »): the Passager fetch could resolve after the
    // deep-link and silently replace the session's client in the sale.
    loadCustomers(!(location.state && location.state.clientId));
    loadCategories();
    loadSubcategories();
    loadClientCategories();
    loadLaboratoires();
    promoService.getActivePromos().then(setActivePromos).catch(() => {});
  }, []);

  // Map productId -> promo price for SINGLE promos (applied automatically in cart).
  const singlePromoMap = useMemo(() => {
    const m = {};
    for (const pr of activePromos) {
      if (pr.type === 'SINGLE' && pr.items && pr.items.length === 1) {
        m[pr.items[0].product] = { price: Math.round((Number(pr.promo_price) || 0) * 1000) / 1000, name: pr.name };
      }
    }
    return m;
  }, [activePromos]);

  // « Remise à tous les produits » — an active ALL promo applies a % off every
  // product. If several are active, the biggest percent wins.
  const allPromo = useMemo(() => {
    const alls = activePromos.filter((p) => p.type === 'ALL' && Number(p.percent) > 0);
    if (!alls.length) return null;
    return alls.reduce((a, b) => (Number(b.percent) > Number(a.percent) ? b : a));
  }, [activePromos]);

  // Effective promo for a product: an explicit SINGLE promo wins, otherwise the
  // global ALL promo applies its percent to the product's price. Returns
  // { price, name } or null. Used for the badge AND the cart price.
  const promoFor = React.useCallback((product) => {
    const sp = singlePromoMap[product.id];
    if (sp) return sp;
    if (allPromo) {
      const price = Math.round((Number(product.price) || 0) * (1 - Number(allPromo.percent) / 100) * 1000) / 1000;
      return { price, name: allPromo.name };
    }
    return null;
  }, [singlePromoMap, allPromo]);

  // Active packs (shown as their own cards in the catalogue).
  const packs = useMemo(() => activePromos.filter((p) => p.type === 'PACK' && (p.items || []).length > 0), [activePromos]);

  // Load laboratoires for the product filter
  const loadLaboratoires = async () => {
    try {
      const data = await laboratoireService.getAllLaboratoires();
      setLaboratoires(Array.isArray(data) ? data : (data.results || []));
    } catch (err) {
      console.error('Error loading laboratoires:', err);
    }
  };

  // Refresh sessions whenever the Services tab is shown so a newly-created
  // rendez-vous is reflected immediately when picking soins.
  useEffect(() => {
    if (catalogTab === 'services') loadSessions();
  }, [catalogTab]);

  // --- Produit bloqué pour LE client sélectionné → vendable en vente normale ---
  // Une réservation « Bloquée » sort ses unités du stock : le produit disparaît
  // de la recherche pour tout le monde. Quand le client sélectionné est CELUI
  // pour qui c'est bloqué (téléphone identique, sinon nom complet identique),
  // ses unités bloquées redeviennent vendables ici, et la vente clôturera la
  // réservation côté serveur (reservation_ids).
  const normPhone = (s) => String(s || '').replace(/\D/g, '');
  const normName = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const clientBlockedBonus = useMemo(() => {
    const map = {};
    if (!selectedCustomer || selectedCustomer.id === 5) return map;
    const phone = normPhone(selectedCustomer.phone);
    const fullName = normName(`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`);
    blockedReservations.forEach((r) => {
      if (!r.product) return; // hors-catalogue : rien à vendre ici
      const rPhone = normPhone(r.customer_phone);
      const match = (phone && rPhone)
        ? rPhone === phone
        : (!!fullName && normName(r.customer_name) === fullName);
      if (!match) return;
      const e = map[r.product] || { qty: 0, ids: [], deposit: 0 };
      e.qty += r.quantity || 0;
      e.ids.push(r.id);
      e.deposit += Number(r.deposit_amount) || 0;
      map[r.product] = e;
    });
    return map;
  }, [blockedReservations, selectedCustomer]);

  // Stock vendable pour CE client = stock libre + ses unités bloquées.
  const sellableStock = (product) =>
    (product?.stock || 0) + (clientBlockedBonus[product?.id]?.qty || 0);

  // Filtered products based on search and filters. La recherche suit la règle
  // Ki Para (utils/search.js) : début de mot, sans accents, « commence par »
  // en tête ; code / code-barres « contient ».
  const filteredProducts = useMemo(() => {
    const list = filterSearch(products, productSearchQuery, {
    text: (p) => p.name, codes: (p) => [p.code, p.barcode],
  }).filter(product => {
    // Stock filter: products out of stock are never shown in the sales search —
    // except the client's own blocked units, which count as sellable for them.
    // En mode retour, tout produit peut revenir — même s'il n'est plus en stock.
    const inStock = sellableStock(product) > 0;
    if (!inStock && !returnMode) return false;

    // Category filter
    const matchesCategory = !selectedCategoryFilter || 
      (product.category && 
        (typeof product.category === 'object' ? product.category.id === parseInt(selectedCategoryFilter) : product.category === parseInt(selectedCategoryFilter)));
    
    // Subcategory filter
    const matchesSubcategory = !selectedSubcategoryFilter ||
      (product.subcategory &&
        (typeof product.subcategory === 'object' ? product.subcategory.id === parseInt(selectedSubcategoryFilter) : product.subcategory === parseInt(selectedSubcategoryFilter)));

    // Laboratoire filter
    const matchesLab = !labFilter ||
      (product.laboratoire &&
        (typeof product.laboratoire === 'object' ? product.laboratoire.id === parseInt(labFilter) : product.laboratoire === parseInt(labFilter)));

    return matchesCategory && matchesSubcategory && matchesLab;
  });

  // Produits en promo « en tête de liste » : un produit avec une promo active
  // (SINGLE, ou la remise globale ALL) remonte en premier dans la grille — au
  // lieu d'être affiché dans une rangée de cartes séparée. Le tri est stable,
  // donc l'ordre normal est conservé au sein de chaque groupe.
    list.sort((a, b) => (promoFor(b) ? 1 : 0) - (promoFor(a) ? 1 : 0));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, productSearchQuery, selectedCategoryFilter, selectedSubcategoryFilter, labFilter, returnMode, clientBlockedBonus, promoFor]);

  // Perf (2026-10-05) : la grille ne monte que les premières cartes (60), puis
  // 60 de plus chaque fois que le bas de la liste arrive à l'écran — au lieu de
  // 1 000+ cartes et leurs photos d'un coup (14 000 nœuds, ~3 s de JS bloqué).
  // Même cartes, même ordre : rien ne change à l'écran.
  const GRID_STEP = 60;
  const [gridLimit, setGridLimit] = useState(GRID_STEP);
  const gridEndRef = useRef(null);
  useEffect(() => { setGridLimit(GRID_STEP); }, [productSearchQuery, selectedCategoryFilter, selectedSubcategoryFilter, labFilter, returnMode]);
  useEffect(() => {
    const el = gridEndRef.current;
    if (!el || gridLimit >= filteredProducts.length || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setGridLimit((n) => n + GRID_STEP);
    }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [gridLimit, filteredProducts.length]);
  const visibleProducts = gridLimit < filteredProducts.length ? filteredProducts.slice(0, gridLimit) : filteredProducts;

  // Products out of stock (stock 0) — surfaced in the "Passer une commande" popup,
  // since they are intentionally hidden from the sales catalogue above.
  const outOfStockProducts = products.filter((p) => Number(p.stock || 0) === 0);

  // Popup list : sans recherche = les ruptures ; AVEC recherche = TOUT le
  // catalogue (en stock compris). Un client qui réserve 2 produits dont un
  // seul est en stock veut tout prendre ensemble → on réserve aussi celui
  // qui est en stock (le vendeur le bloque ensuite depuis Réservations).
  const orderSearchProducts = orderSearch.trim()
    ? filterSearch(products, orderSearch, { text: (p) => p.name, codes: (p) => [p.code, p.barcode] })
    : outOfStockProducts;

  const isReserveSelected = (product) => reserveSelected.some((p) => p.id === product.id);

  // Toggle a product in the multi-select grid.
  const toggleReserveProduct = (product) => {
    setReserveSelected((prev) =>
      prev.some((p) => p.id === product.id)
        ? prev.filter((p) => p.id !== product.id)
        : [...prev, product]
    );
  };

  // Close the "Passer une commande" popup and reset its reservation state.
  const closeOrderModal = () => {
    setShowOrderModal(false);
    setReserveStep('select');
    setReserveSelected([]);
    setReserveLines([]);
    setOrderSearch('');
    setShowNewProductForm(false);
    setNewProduct({ name: '', cost: '' });
    setShowOrderClients(false);
    setOrderClientResults([]);
  };

  // Move from the product-selection step to the reservation form (one line per
  // selected product); prefill customer from the page's selected client if real.
  // Takes the selection list explicitly so a just-added new product is included
  // (setState is async, so reserveSelected wouldn't reflect it yet).
  const enterReserveForm = (list) => {
    if (list.length === 0) return;
    const isReal = selectedCustomer && !isPassagerCustomer();
    setReserveForm({
      customer_name: isReal ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() : '',
      customer_phone: isReal ? (selectedCustomer.phone || '') : '',
      expected_arrival_date: '',
      note: '',
    });
    setReserveLines(list.map((p) => ({
      id: p.id,
      name: p.name,
      image: p.image || null,
      price: p.isNew ? (Number(p.estimated_sale_price) || 0) : (Number(p.price) || 0),
      quantity: 1,
      deposit_amount: '',
      isNew: !!p.isNew,
      estimated_cost: p.estimated_cost || 0,
      estimated_sale_price: p.estimated_sale_price || 0,
    })));
    setReserveStep('form');
  };
  const goToReserveForm = () => enterReserveForm(reserveSelected);

  // Local rounding (round3 is declared later in the component → avoid its TDZ here).
  const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
  // Estimated selling price for the "+ Nouveau produit" form (cost × TVA × marge).
  const newProductSalePrice = r3((parseFloat(newProduct.cost) || 0) * (1 + TVA_RATE) * (1 + DEFAULT_MARGIN_RATE));

  // Détection de doublon pendant la saisie du nom (« + Nouveau produit ») :
  // correspondance exacte (nom normalisé) ou produits proches du catalogue.
  const newProductMatches = (() => {
    if (!showNewProductForm) return { exact: null, similar: [] };
    const q = normProductName(newProduct.name);
    if (q.length < 2) return { exact: null, similar: [] };
    let exact = null;
    const similar = [];
    for (const p of products) {
      const n = normProductName(p.name);
      if (n === q) { if (!exact) exact = p; continue; }
      if (q.length >= 3 && similar.length < 3 && (n.includes(q) || q.includes(n))) similar.push(p);
    }
    return { exact, similar };
  })();

  // « Utiliser ce produit » : réserver la vraie fiche catalogue (rupture de
  // stock) au lieu de créer une réservation hors catalogue du même nom.
  const pickExistingReserveProduct = (p) => {
    if (Number(p.stock || 0) > 0) {
      // En stock → pas de réservation : il se vend directement au catalogue.
      duplicateProductAlert({ product: p, name: p.name });
      return;
    }
    const next = reserveSelected.some((x) => x.id === p.id) ? reserveSelected : [...reserveSelected, p];
    setReserveSelected(next);
    setNewProduct({ name: '', cost: '' });
    setShowNewProductForm(false);
    enterReserveForm(next);
  };

  // Add a brand-new (off-catalogue) product as a reservation line, then jump to
  // the reservation form. The product itself is NOT created in the catalogue —
  // only its name + estimated prices are carried onto the reservation.
  const addNewProductReserve = () => {
    const nm = newProduct.name.trim();
    const cost = parseFloat(newProduct.cost);
    if (!nm) { Swal.fire('Erreur', 'Le nom du produit est requis', 'error'); return; }
    // Même nom qu'un produit du catalogue → carte « Produit déjà au
    // catalogue » : utiliser sa fiche (si en rupture, donc réservable) ou
    // modifier le nom pour les distinguer.
    const dup = newProductMatches.exact;
    if (dup) {
      const canReserve = Number(dup.stock || 0) === 0;
      duplicateProductAlert({
        product: dup, name: nm,
        confirmText: canReserve ? 'Utiliser le produit existant' : undefined,
      }).then((r) => {
        if (r.isConfirmed && canReserve) {
          pickExistingReserveProduct(dup);
        } else {
          setTimeout(() => { newProductNameRef.current?.focus(); newProductNameRef.current?.select(); }, 50);
        }
      });
      return;
    }
    if (isNaN(cost) || cost <= 0) { Swal.fire('Erreur', "Entrez un prix d'achat estimé valide", 'error'); return; }
    newProductSeqRef.current += 1;
    const item = {
      id: `new-${newProductSeqRef.current}`,
      name: nm,
      isNew: true,
      estimated_cost: r3(cost),
      estimated_sale_price: r3(cost * (1 + TVA_RATE) * (1 + DEFAULT_MARGIN_RATE)),
    };
    const next = [...reserveSelected, item];
    setReserveSelected(next);
    setNewProduct({ name: '', cost: '' });
    setShowNewProductForm(false);
    enterReserveForm(next);
  };

  // Search existing clients for the reservation "Nom du client" field.
  const searchOrderClients = async (q) => {
    const query = (q || '').trim();
    if (!query) { setOrderClientResults([]); setShowOrderClients(false); return; }
    try {
      const data = await customerService.searchCustomers(query);
      const list = Array.isArray(data) ? data : (data?.results || []);
      // Exclude the walk-in "Client Passager" (id 5) — it's not a real client.
      setOrderClientResults(list.filter((c) => c.id !== 5).slice(0, 8));
      setShowOrderClients(true);
    } catch (err) {
      setOrderClientResults([]);
    }
  };

  // Pick an existing client → fill name + phone (phone is what lets the later
  // "Vendre au client" preselect them if they hold a fidélité card).
  const pickOrderClient = (c) => {
    setReserveForm((f) => ({
      ...f,
      customer_name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      customer_phone: c.phone || '',
    }));
    setShowOrderClients(false);
    setOrderClientResults([]);
  };

  const updateReserveLine = (id, field, value) => {
    setReserveLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const removeReserveLine = (id) => {
    setReserveLines((prev) => prev.filter((l) => l.id !== id));
    setReserveSelected((prev) => prev.filter((p) => p.id !== id));
  };

  // Live totals for the reservation form summary strip.
  const reserveTotal = reserveLines.reduce((s, l) => s + (Number(l.price) || 0) * (Math.max(1, Number(l.quantity) || 1)), 0);
  const reserveAvance = reserveLines.reduce((s, l) => s + (Number(l.deposit_amount) || 0), 0);
  const reserveReste = Math.max(0, reserveTotal - reserveAvance);

  // The "Total avance" strip field is editable: the seller types the total the
  // client hands over and it is split across lines proportionally to each
  // line's total (rounding difference absorbed by the last line). While the
  // field has focus, the raw draft string is shown so typing isn't fought by
  // the recomputed sum.
  const [avanceEditing, setAvanceEditing] = useState(false);
  const [avanceDraft, setAvanceDraft] = useState('');
  const distributeAvance = (totalStr) => {
    const total = Math.max(0, parseFloat(totalStr) || 0);
    setReserveLines((prev) => {
      const lineTotals = prev.map((l) => (Number(l.price) || 0) * Math.max(1, Number(l.quantity) || 1));
      const grand = lineTotals.reduce((a, b) => a + b, 0);
      // Le total d'avance saisi est plafonné au total de la réservation.
      let remaining = r3(grand > 0 ? Math.min(total, grand) : total);
      return prev.map((l, i) => {
        let share;
        if (i === prev.length - 1) {
          share = remaining;
        } else if (grand > 0) {
          share = Math.min(r3(total * lineTotals[i] / grand), remaining);
        } else {
          share = i === 0 ? remaining : 0;
        }
        remaining = r3(remaining - share);
        return { ...l, deposit_amount: share > 0 ? String(share) : '' };
      });
    });
  };

  const handleCreateReservation = async (e) => {
    e.preventDefault();
    if (!reserveForm.customer_name.trim()) {
      Swal.fire('Erreur', 'Le nom du client est requis', 'error');
      return;
    }
    if (reserveLines.length === 0) return;
    // Une avance exige un prix connu, et ne peut jamais dépasser le prix total
    // de sa ligne (trop-perçu interdit — le backend refuse les deux aussi).
    const noPrice = reserveLines.find((l) =>
      (Number(l.deposit_amount) || 0) > 0 &&
      ((Number(l.price) || 0) * Math.max(1, Number(l.quantity) || 1)) <= 0);
    if (noPrice) {
      Swal.fire('Prix manquant',
        `« ${noPrice.name} » n'a pas de prix — saisissez le coût estimé (qui calcule le prix) avant d'encaisser une avance.`,
        'error');
      return;
    }
    const over = reserveLines.find((l) => {
      const lineTotal = (Number(l.price) || 0) * Math.max(1, Number(l.quantity) || 1);
      return lineTotal > 0 && (Number(l.deposit_amount) || 0) > lineTotal + 0.0005;
    });
    if (over) {
      Swal.fire('Avance trop élevée',
        `L'avance de « ${over.name} » dépasse le prix total du produit. Réduisez l'avance.`,
        'error');
      return;
    }
    // Qui réserve ? (code sur le compte vendeur) — annulé = rien.
    const reserveWho = await requestIdentity({ title: 'Qui réserve ?' });
    if (reserveWho === false) return;
    const reserveDone = stampAs(reserveWho);
    setReserveSubmitting(true);
    try {
      // One reservation per selected product, sharing the customer details.
      await Promise.all(reserveLines.map((line) =>
        reservationService.createReservation({
          product: line.isNew ? null : line.id,   // new (off-catalogue) products have no product id
          product_name: line.name,
          customer_name: reserveForm.customer_name.trim(),
          customer_phone: reserveForm.customer_phone.trim() || null,
          quantity: parseInt(line.quantity, 10) || 1,
          expected_arrival_date: reserveForm.expected_arrival_date || null,
          deposit_amount: parseFloat(line.deposit_amount) || 0,
          estimated_cost: line.estimated_cost || 0,
          estimated_sale_price: line.estimated_sale_price || 0,
          note: reserveForm.note.trim() || '',
        })
      ));
      Swal.fire({
        icon: 'success',
        title: 'Réservé',
        text: reserveLines.length > 1 ? `${reserveLines.length} produits réservés.` : 'La réservation a été enregistrée.',
        timer: 1600,
        showConfirmButton: false,
      });
      closeOrderModal();
    } catch (err) {
      console.error('Error creating reservation(s):', err);
      Swal.fire('Erreur', "Erreur lors de l'enregistrement de la réservation.", 'error');
    } finally {
      reserveDone();
      setReserveSubmitting(false);
    }
  };

  // ---- "Réserver un soin" (rendez-vous / session) ----
  const soinServicesList = services.filter((s) => s.type === 'SOIN');

  const PERIOD_TIME = { matin: '09:00', apresmidi: '14:00' };
  const PERIOD_LABEL = { matin: 'Matin', apresmidi: 'Après-midi' };

  // Build the scheduled_time string ("YYYY-MM-DDTHH:mm") from the chosen day + period.
  const soinScheduledTime = () => {
    if (!soinDay || !soinPeriod) return '';
    return `${soinDay}T${PERIOD_TIME[soinPeriod]}`;
  };

  const openSoinModal = () => {
    const now = new Date();
    const isReal = selectedCustomer && !isPassagerCustomer();
    setSoinStep('select');
    setSoinForm({ service: hiddenSoins[0]?.id || soinServicesList[0]?.id || '', notes: '' });
    setSoinCustomer(isReal ? selectedCustomer : null);
    setSoinCustomerQuery('');
    setSoinCustomers([]);
    setShowSoinCustomerDropdown(false);
    setSoinCalMonth({ year: now.getFullYear(), month: now.getMonth() });
    setSoinDay('');
    setSoinPeriod('');
    setSoinPayment('unpaid');
    setSoinPayWithPoints(false);
    setShowSoinModal(true);
  };

  // Deep-link from the dashboard quick actions: open the matching popup on arrival.
  //  - location.state.openSoin  → "Réserver un soin" modal
  //  - location.state.commande  → "Passer une commande" (réapprovisionnement) modal
  useEffect(() => {
    if (!location.state) return;
    if (location.state.openSoin) {
      openSoinModal();
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state.commande) {
      setShowOrderModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Deep-link from "Terminer" on a dashboard session: add the soin to the cart at
  // its BOOKED price/remise, select the session's client, open the panier so the
  // seller can collect payment and add products to the same sale.
  // Waits until services load.
  useEffect(() => {
    const st = location.state || {};
    if (!st.addServiceId || services.length === 0) return;
    if (st.soinPaid) {
      // Soin already paid at booking — never charge it twice. The client is still
      // selected and the panier opens so products can be sold to them.
      Swal.fire({
        icon: 'info',
        title: 'Soin déjà payé',
        text: 'Ce soin a été réglé à la réservation — il n\'est pas refacturé. Vous pouvez ajouter des produits.',
        timer: 3200,
        showConfirmButton: false,
      });
    } else {
      // The catalogue may no longer list the booked service (deactivated since) —
      // fall back to a line built from the session so the soin is still charged.
      const svc = services.find((s) => String(s.id) === String(st.addServiceId))
        || { id: parseInt(st.addServiceId, 10), name: st.serviceName || 'Soin', type: 'SOIN', price: 0, remise: 0 };
      addService({
        ...svc,
        price: st.servicePrice != null ? (parseFloat(st.servicePrice) || 0) : svc.price,
        remise: st.serviceRemise != null ? (parseFloat(st.serviceRemise) || 0) : svc.remise,
      });
    }
    if (st.clientId) selectCustomer({ id: st.clientId });
    // Unpaid session: it is only marked « completed » once THIS sale is
    // encaissée (see handleSubmitSale) — a paid one was completed on the
    // dashboard already.
    if (st.sessionId && !st.soinPaid) setPendingSessionId(st.sessionId);
    setShowPanier(true);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, location.state]);

  // Deep-link from "Vendre au client" on a reservation: add the reserved product
  // to the panier at its reserved quantity, preselect the reserved client (found
  // or created server-side), open the panier, and remember the reservation so it
  // is marked "Vendue" once the sale is finalized. Waits until products load.
  useEffect(() => {
    const st = location.state || {};
    if (!st.addProductId || products.length === 0) return;
    const product = products.find((p) => String(p.id) === String(st.addProductId));
    if (product) {
      const qty = Math.max(1, parseInt(st.productQty, 10) || 1);
      const promo = promoFor(product);
      const stockCap = product.stock || qty; // don't exceed on-hand stock
      setSelectedProducts((prev) => {
        const existing = prev.find((p) => p.id === product.id);
        if (existing) {
          return prev.map((p) =>
            p.id === product.id
              ? { ...p, quantity: Math.min((p.quantity || 0) + qty, stockCap) }
              : p
          );
        }
        return [...prev, {
          ...product,
          quantity: Math.min(qty, stockCap),
          price: promo ? promo.price : (product.price || 0),
          promo: promo ? promo.name : null,
          remisePct: promo ? 0 : discount,
          remiseEdited: false,
        }];
      });
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Produit introuvable',
        text: 'Le produit réservé n\'est plus au catalogue — ajoutez-le manuellement.',
        timer: 3200,
        showConfirmButton: false,
      });
    }
    if (st.clientId) selectCustomer({ id: st.clientId });
    if (st.reservationId) setPendingReservationId(st.reservationId);
    const avance = parseFloat(st.reservationDeposit) || 0;
    setReservationAvance(avance);
    if (avance > 0) {
      Swal.fire({
        icon: 'info',
        title: 'Avance déjà payée',
        text: `${avance.toFixed(3)} DT encaissés à la réservation — déduits du reste à payer.`,
        timer: 3000,
        showConfirmButton: false,
      });
    }
    setShowPanier(true);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, location.state]);

  const shiftSoinMonth = (delta) => {
    setSoinCalMonth((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const searchSoinCustomers = async (q) => {
    if (!q || q.trim().length === 0) { setSoinCustomers([]); return; }
    try {
      const data = await customerService.searchCustomers(q);
      setSoinCustomers(Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []));
    } catch (err) {
      console.error('Error searching customers:', err);
    }
  };

  // Step 1 → 2: validate the selection, then move to the confirm/payment step.
  const goToSoinConfirm = () => {
    if (!soinForm.service) { Swal.fire('Erreur', 'Veuillez choisir un soin', 'error'); return; }
    if (!soinCustomer) { Swal.fire('Erreur', 'Veuillez choisir un client', 'error'); return; }
    if (!soinDay || !soinPeriod) { Swal.fire('Erreur', 'Veuillez choisir un jour puis matin ou après-midi', 'error'); return; }
    setSoinStep('confirm');
  };

  const handleCreateSoinReservation = async (e) => {
    e.preventDefault();
    const scheduled = soinScheduledTime();
    if (!scheduled || !soinForm.service || !soinCustomer) { setSoinStep('select'); return; }
    const soinWho = await requestIdentity({ title: 'Qui réserve le soin ?' });
    if (soinWho === false) return;
    const soinDone = stampAs(soinWho);
    setSoinSubmitting(true);
    try {
      // Soin paid with points at booking. The soin's price IN POINTS comes from the
      // client's category "Avantages" (Gestion des Catégories Client), NOT 1 pt = 1 DT.
      const soin = soinServicesList.find((s) => s.id === parseInt(soinForm.service, 10));
      const availPts = round3(parseFloat(soinCustomer.points) || 0);
      const cost = soinPointsCost(categoryById(soinCustomer.category), soin); // null → not configured
      const usingPoints = soinPayWithPoints && soinCustomer.id !== 5 && cost !== null && availPts >= cost;
      const pointsUsed = usingPoints ? cost : 0;
      // Paid with points → "Payé"; otherwise keep the Payé/À payer choice.
      const finalPaymentStatus = usingPoints ? 'paid' : soinPayment;

      await serviceManagementService.createSession({
        service: parseInt(soinForm.service, 10),
        customer: soinCustomer.id,
        scheduled_time: scheduled,
        status: 'scheduled',
        payment_status: finalPaymentStatus,
        notes: soinForm.notes || '',
      });
      // Deduct the spent points from the client's balance.
      if (pointsUsed > 0) {
        try {
          await customerService.patchCustomer(soinCustomer.id, { points: round3(availPts - pointsUsed) });
        } catch (e2) {
          console.error('Error deducting soin points:', e2);
        }
      }
      Swal.fire({ icon: 'success', title: 'Soin réservé', text: pointsUsed > 0 ? `Rendez-vous créé · ${round3(pointsUsed)} points utilisés.` : 'Le rendez-vous a été créé.', timer: 1800, showConfirmButton: false });
      setShowSoinModal(false);
      loadSessions(); // refresh so the soin is no longer masqué for this client
    } catch (err) {
      console.error('Error creating soin reservation:', err);
      // Surface the REAL backend reason instead of a generic message.
      const data = err?.response?.data;
      let msg = data?.error || data?.detail;
      if (!msg && data && typeof data === 'object') {
        const first = Object.values(data)[0];
        msg = Array.isArray(first) ? first[0] : (typeof first === 'string' ? first : null);
      }
      if (!msg && err?.response?.status) msg = `Erreur ${err.response.status}`;
      Swal.fire('Erreur', msg || "Erreur lors de la réservation du soin.", 'error');
    } finally {
      soinDone();
      setSoinSubmitting(false);
    }
  };

  // Calculate totals
  const productsSubtotal = selectedProducts.reduce((sum, product) => sum + (product.price * product.quantity), 0);
  // Per-line product remise (%). SOINS and PROMO items have NO remise, so promo
  // product lines are skipped here (and excluded from the global remise base below).
  const productLineRemiseTotal = selectedProducts.reduce((sum, product) => {
    if (product.promo) return sum; // promo line → no remise
    const lineGross = product.price * product.quantity;
    const pct = Math.min(Math.max(parseFloat(product.remisePct) || 0, 0), 100);
    return sum + (lineGross * pct / 100);
  }, 0);
  const servicesSubtotal = selectedServices.reduce((sum, service) => {
    if (isServiceGratuit(service) || isServicePaidWithPoints(service)) return sum; // 0 DT: comped, or settled in points
    const price = parseFloat(service.price) || 0;
    const remise = parseFloat(service.remise) || 0;
    const finalPrice = remise > 0 ? price * (1 - remise / 100) : price;
    return sum + (finalPrice * (service.quantity || 1));
  }, 0);
  // sub_total mirrors the backend: full product price × qty + service price after its %.
  // round3 kills floating-point garbage (e.g. 350.0180000000003 -> 350.018).
  const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
  const subtotal = round3(productsSubtotal + servicesSubtotal);
  // The remise is realized entirely through the per-line product remises. The
  // "global remise %" is just a bulk default that seeds those lines (see effect
  // below) — it is NOT an extra discount applied on top, so no double-counting.
  const totalRemise = round3(productLineRemiseTotal);
  const totalAmount = round3(Math.max(0, subtotal - totalRemise)); // = total à payer

  // Loyalty points used as payment (1 pt = 1 DT). Paying PRODUCTS with points is
  // reserved to "fidèle gold" clients, and only when the balance is at least 30
  // points. Partial is allowed; the rest is paid in cash. pointsPay is clamped to
  // the balance and to the total.
  const POINTS_MIN_USE = 30;
  const isGoldCustomer = !!customerCategory && (customerCategory.name || '').toLowerCase().includes('gold');
  const pointsBalance = round3(parseFloat(selectedCustomer?.points) || 0);
  // Points committed to SOIN lines toggled « payé avec points » (category cost × qty).
  const soinsPointsSpend = round3(selectedServices.reduce((sum, s) =>
    isServicePaidWithPoints(s) ? sum + (servicePointsCost(s) || 0) * (s.quantity || 1) : sum, 0));
  // The gold 1 pt = 1 DT tender draws on what remains AFTER the soin costs.
  const pointsBalanceAfterSoins = round3(Math.max(0, pointsBalance - soinsPointsSpend));
  const pointsAvailable = (selectedCustomer && !isPassagerCustomer() && isGoldCustomer && pointsBalanceAfterSoins >= POINTS_MIN_USE)
    ? pointsBalanceAfterSoins : 0;
  const pointsPay = Math.min(Math.max(0, round3(Number(pointsToUse) || 0)), pointsAvailable, totalAmount);
  // Réservations bloquées de CE client réellement consommées par le panier
  // (quantité demandée > stock libre) : elles seront clôturées par le backend
  // (reservation_ids) et leur avance vient en déduction du reste à payer.
  const consumedBlockedReservations = useMemo(() => {
    const out = [];
    selectedProducts.forEach((p) => {
      const bonus = clientBlockedBonus[p.id];
      if (!bonus) return;
      const freeStock = products.find((x) => x.id === p.id)?.stock || 0;
      if ((p.quantity || 0) > freeStock) out.push(bonus);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts, clientBlockedBonus, products]);
  const autoBlockedAvance = round3(consumedBlockedReservations.reduce((t, b) => t + (b.deposit || 0), 0));
  // Avance totale = avance du flux « Vendre au client » + avances des
  // réservations bloquées consommées par ce panier.
  const totalAvance = round3((Number(reservationAvance) || 0) + autoBlockedAvance);
  // Avance réservation clampée : jamais plus que ce qui reste après les points.
  const avancePay = round3(Math.min(Math.max(0, totalAvance), Math.max(0, totalAmount - pointsPay)));
  // Trop-perçu : avance encaissée > total de la vente (prix baissé, ancienne
  // réservation…) → à RENDRE au client ; envoyé au backend qui l'enregistre
  // en monnaie rendue.
  const avanceSurplus = round3(Math.max(0, totalAvance - avancePay));
  const dueAfterPoints = round3(Math.max(0, totalAmount - pointsPay - avancePay)); // cash still owed

  // Auto-fill the cash amount paid to the remaining due (after points) — UNLESS the
  // cashier has manually edited it (so a manual 0 "client ne paie pas" sticks).
  useEffect(() => {
    if (!amountPaidTouched) setAmountPaid(dueAfterPoints);
  }, [dueAfterPoints, amountPaidTouched]);

  // Reset points-to-use and the "touched" flag whenever the client changes.
  // Also refresh the blocked reservations so a product bloqué pour ce client
  // (re)apparaît immédiatement dans le catalogue.
  useEffect(() => {
    setPointsToUse(0);
    setPointsToUseRaw(null);
    setAmountPaidTouched(false);
    // The soin points cost comes from the client's category — a « payé avec
    // points » toggle from the previous client no longer applies.
    setSelectedServices(prev => (prev.some(s => s.payWithPoints)
      ? prev.map(s => (s.payWithPoints ? { ...s, payWithPoints: false } : s))
      : prev));
    loadBlockedReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id]);

  // The global remise % defaults to the client's facture/category remise. The
  // cashier can override it; either way it flows down to the lines via the effect
  // below. (Passager → 5%, others → their category remise.)
  useEffect(() => {
    setDiscount(factureRemisePercent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factureRemisePercent]);

  // Global remise % is the DEFAULT for every product line: it seeds the per-line
  // remise of every non-promo line the cashier hasn't manually edited. Manually
  // edited lines (remiseEdited) keep their own % and are no longer driven by the
  // global value. This is the "global = default, individual lines override" link.
  useEffect(() => {
    setSelectedProducts(prev => {
      let changed = false;
      const next = prev.map(p => {
        if (p.promo) return p; // promo lines never get a remise
        if (p.remiseEdited) return p;
        if ((parseFloat(p.remisePct) || 0) === discount) return p;
        changed = true;
        return { ...p, remisePct: discount };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount]);

  // Load products
  const loadProducts = async () => {
    // Paint the last known catalog at once (memory, then this device) — the
    // fresh answer below replaces it; the server re-checks stock on sale.
    const early = peekCached('products/?page_size=5000');
    const earlyList = Array.isArray(early) ? early : (early?.results || []);
    if (earlyList.length) setProducts((prev) => (prev.length ? prev : earlyList));
    else lastKnownCatalog().then((list) => { if (list?.length) setProducts((prev) => (prev.length ? prev : list)); }).catch(() => {});
    try {
      const data = await productService.getAllProducts();
      // productService.getAllProducts already extracts the results array
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading products:', err);
      Swal.fire({
        title: 'Erreur',
        text: 'Erreur lors du chargement des produits',
        icon: 'error',
        confirmButtonColor: KI.coralDark
      });
    }
  };

  // Load services
  const loadServices = async () => {
    try {
      const response = await serviceManagementService.getAllServices();
      const servicesList = response.results || response || [];
      // Filter only active services
      const activeServices = Array.isArray(servicesList) ? servicesList.filter(s => s.is_active) : [];
      setServices(activeServices);
    } catch (err) {
      console.error('Error loading services:', err);
      Swal.fire({
        title: 'Erreur',
        text: 'Erreur lors du chargement des services',
        icon: 'error',
        confirmButtonColor: KI.coralDark
      });
    }
  };

  // Load sessions (rendez-vous) used to gate SOIN services
  const loadSessions = async () => {
    try {
      const res = await serviceManagementService.getAllSessions();
      const list = Array.isArray(res) ? res : (res.results || []);
      setSessions(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  // A service is sellable to the current client when:
  //  - it is NOT a SOIN (Diagnostique / Acte pass freely), OR
  //  - the selected client has an ACTIVE rendez-vous (scheduled / in progress)
  //    for that exact soin, not yet encaissé (no linked sale) nor paid at
  //    booking. Completed sessions don't count — otherwise a client who booked
  //    once would see the soin forever. The "Terminer" deep-link adds its soin
  //    directly, whatever this filter says.
  const hasRendezVous = (service) => {
    if (service.type !== 'SOIN') return true;
    if (!selectedCustomer) return false;
    return sessions.some(se =>
      String(se.customer) === String(selectedCustomer.id) &&
      String(se.service) === String(service.id) &&
      (se.status === 'scheduled' || se.status === 'in_progress') &&
      !se.sale && se.payment_status !== 'paid'
    );
  };

  // Load customers
  const loadCustomers = async (autoSelectPassager = false) => {
    const early = peekCached('customers/?page_size=5000');
    if (Array.isArray(early?.results) && early.results.length) setCustomers((prev) => (prev.length ? prev : early.results));
    try {
      const data = await customerService.getAllCustomers();
      const customersList = Array.isArray(data.results) ? data.results : [];
      setCustomers(customersList);
      
      // Set default customer to "Passager" (id=5) only on initial load
      if (autoSelectPassager && !selectedCustomer) {
        const passagerCustomer = customersList.find(c => c.id === 5);
        if (passagerCustomer) {
          // Use selectCustomer to properly set up category and discount
          selectCustomer(passagerCustomer);
        }
      }
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  // Load categories
  const loadCategories = async () => {
    try {
      const response = await categoryService.getAllCategories();
      const data = response.results || response || [];
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  // Load subcategories
  const loadSubcategories = async () => {
    try {
      const response = await subcategoryService.getAllSubcategories();
      const data = response.results || response || [];
      setSubcategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading subcategories:', err);
    }
  };

  // Load client categories
  const loadClientCategories = async () => {
    try {
      const response = await clientCategoryService.getAllCategories();
      const data = response.results || response || [];
      const sortedCategories = Array.isArray(data) 
        ? data.sort((a, b) => a.required_score - b.required_score) 
        : [];
      setAllCategories(sortedCategories);
    } catch (err) {
      console.error('Error loading client categories:', err);
    }
  };
  
  // Filter subcategories based on selected category
  const filteredSubcategories = selectedCategoryFilter
    ? subcategories.filter(sub => sub.category_id === parseInt(selectedCategoryFilter))
    : subcategories;

  // Services shown in the dialog: name match AND (non-soin OR client has a rendez-vous)
  const serviceMatchesSearch = (service) => matchesSearch(serviceSearchQuery, service.name);
  const filteredServices = services.filter(s => serviceMatchesSearch(s) && hasRendezVous(s));
  // Soins hidden purely because the current client has no rendez-vous (used for the info banner)
  const hiddenSoins = services.filter(s => serviceMatchesSearch(s) && s.type === 'SOIN' && !hasRendezVous(s));

  // Calculate points to be gained. Points are earned on the GROSS total
  // (price × qty, before any remise — line, global or service %): the remise
  // must never reduce the points the client earns. Products AND soins count;
  // a "gratuit" (comped) service is sent at 0 DT so it earns nothing.
  // Display only — the actual crediting happens server-side at sale creation.
  const calculatePointsGain = () => {
    if (!customerCategory || isPassagerCustomer()) return 0;
    const servicesGross = selectedServices.reduce((sum, s) =>
      sum + ((isServiceGratuit(s) || isServicePaidWithPoints(s)) ? 0 : (parseFloat(s.price) || 0) * (s.quantity || 1)), 0);
    return (productsSubtotal + servicesGross) * (customerCategory.points_value / 100);
  };

  // Get next category based on new score
  const getNextCategory = (currentScore) => {
    if (allCategories.length === 0) return null;
    
    // Find the first category with required_score > currentScore
    return allCategories.find(cat => cat.required_score > currentScore);
  };

  // Check if customer will upgrade to next category
  const checkCategoryUpgrade = (currentScore, newScore) => {
    const nextCategory = getNextCategory(currentScore);
    if (!nextCategory) return null;
    
    // Check if new score reaches the next category threshold
    if (newScore >= nextCategory.required_score) {
      return nextCategory;
    }
    return null;
  };

  // Search customers
  const searchCustomers = async (query) => {
    try {
      // Dès la 1re lettre (règle Ki Para) — avant, sous 2 lettres on
      // réaffichait TOUTE la liste, d'où « h » qui montrait Ahlem et les autres.
      if (query.trim().length >= 1) {
        const data = await customerService.searchCustomers(query);
        setCustomers(Array.isArray(data.results) ? data.results : []);
      } else {
        loadCustomers(false); // Don't auto-select Passager when searching
      }
    } catch (err) {
      console.error('Error searching customers:', err);
    }
  };

  // Add product to cart
  const addToCart = (product) => {
    // Check stock availability (stock libre + unités bloquées pour CE client)
    // (un retour fait REMONTER le stock : pas de plafond en mode retour)
    const cap = returnMode ? Infinity : sellableStock(product);
    if (cap <= 0) {
      Swal.fire({
        title: 'Stock insuffisant',
        text: 'Ce produit n\'est pas disponible en stock',
        icon: 'warning',
        confirmButtonColor: KI.coral
      });
      return;
    }

    const existingProduct = selectedProducts.find(p => p.id === product.id);

    if (existingProduct) {
      // Check if adding one more would exceed stock
      if (existingProduct.quantity >= cap) {
        Swal.fire({
          title: 'Stock insuffisant',
          text: `Stock disponible: ${cap}`,
          icon: 'warning',
          confirmButtonColor: KI.coral
        });
        return;
      }
      
      setSelectedProducts(selectedProducts.map(p => 
        p.id === product.id 
          ? { ...p, quantity: p.quantity + 1 }
          : p
      ));
    } else {
      // Apply an active promo price automatically (SINGLE product promo, or the
      // global « remise à tous les produits » ALL promo).
      const promo = promoFor(product);
      setSelectedProducts([...selectedProducts, {
        ...product,
        quantity: 1,
        price: promo ? promo.price : (product.price || 0),
        promo: promo ? promo.name : null, // promo badge in the panier
        remisePct: promo ? 0 : discount, // new lines inherit the global remise %; promo lines get none
        remiseEdited: false              // becomes true once the cashier touches this line's box
      }]);
    }
    // Dialog stays open so the user can add multiple products
  };

  // Barcode scan / Enter in the product search: a hardware scanner types the code
  // then sends Enter, so on Enter we find the product by EXACT code/barcode (or
  // fall back to the first visible match), add it to the cart, and clear the
  // field so the next scan starts fresh.
  const handleProductSearchEnter = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const q = (e.target.value || '').trim();
    if (!q) return;
    // 1) exact barcode / code match (what the scanner sends)
    let match = products.find(
      (p) =>
        (p.code && String(p.code).toLowerCase() === q.toLowerCase()) ||
        (p.barcode && String(p.barcode) === q)
    );
    // 2) otherwise the first visible (in-stock, filtered) product
    if (!match && filteredProducts.length > 0) match = filteredProducts[0];
    if (!match) {
      Swal.fire({ title: 'Produit introuvable', text: `Aucun produit pour « ${q} »`, icon: 'warning', confirmButtonColor: KI.coral });
      return;
    }
    addToCart(match); // handles stock check + dedup
    setProductSearchQuery(''); // ready for the next scan
  };

  // Add a PACK promo to the cart: its products at the bundle price (distributed
  // proportionally across the lines), so the panier total = the pack price.
  const addPackToCart = (pack) => {
    const items = pack.items || [];
    const resolved = [];
    for (const it of items) {
      const prod = products.find((p) => p.id === it.product);
      if (!prod) { Swal.fire('Erreur', 'Un produit du pack est introuvable.', 'warning'); return; }
      if ((prod.stock || 0) < it.quantity) { Swal.fire('Stock insuffisant', `${prod.name} : stock ${prod.stock || 0}`, 'warning'); return; }
      resolved.push({ prod, qty: it.quantity, base: Number(it.product_price) || Number(prod.price) || 0 });
    }
    const original = resolved.reduce((s, r) => s + r.base * r.qty, 0);
    const factor = original > 0 ? (Number(pack.promo_price) || 0) / original : 1;
    setSelectedProducts((prev) => {
      let next = [...prev];
      resolved.forEach((r) => {
        const unit = Math.round(r.base * factor * 1000) / 1000;
        const ex = next.find((p) => p.id === r.prod.id);
        if (ex) next = next.map((p) => p.id === r.prod.id ? { ...p, quantity: p.quantity + r.qty, price: unit, promo: pack.name } : p);
        else next = [...next, { ...r.prod, quantity: r.qty, price: unit, promo: pack.name, remisePct: 0, remiseEdited: true }];
      });
      return next;
    });
    Swal.fire({ icon: 'success', title: 'Pack ajouté', timer: 1000, showConfirmButton: false });
  };

  // Update a product line's remise (absolute DT, clamped to its line total)
  // Per-line remise typed as a PERCENT (stored as `remisePct`). The totals pipeline
  // derives the DT amount from it. Touching the box marks the line as manually
  // edited so the facture-remise auto-fill no longer overrides it.
  const updateProductRemisePercent = (productId, value) => {
    setSelectedProducts(selectedProducts.map(p => {
      if (p.id !== productId) return p;
      let pct = parseFloat(value);
      if (isNaN(pct) || pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      return { ...p, remisePct: pct, remiseEdited: true };
    }));
  };

  // "Montant à recevoir" (panier): the cashier types the amount they want to
  // collect and we back-derive the global remise % needed to reach it. The remise
  // only moves the lines that still follow the global default — manually edited
  // lines keep their own % (their remise is treated as fixed here). Promo lines and
  // services are never remisable. If every remisable line was edited (or there are
  // none), there's nothing the global % can adjust, so we leave it untouched.
  const setRemiseFromRecevoir = (value) => {
    let target = parseFloat(value);
    if (value === '' || isNaN(target) || target < 0) target = 0;
    let uneditedGross = 0;
    let editedRemise = 0;
    selectedProducts.forEach((p) => {
      if (p.promo) return;
      const gross = p.price * p.quantity;
      if (p.remiseEdited) {
        const pct = Math.min(Math.max(parseFloat(p.remisePct) || 0, 0), 100);
        editedRemise += gross * pct / 100;
      } else {
        uneditedGross += gross;
      }
    });
    if (uneditedGross <= 0) return; // no line the global % can move
    const maxRecevoir = round3(subtotal - editedRemise); // global remise = 0
    if (target > maxRecevoir) target = maxRecevoir;
    if (target < round3(maxRecevoir - uneditedGross)) target = round3(maxRecevoir - uneditedGross);
    let pct = (maxRecevoir - target) / uneditedGross * 100;
    if (pct < 0) pct = 0;
    if (pct > 100) pct = 100;
    setDiscount(round3(pct));
  };

  // Update product quantity
  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    } else {
      // Find the product to check stock (libre + bloqué pour ce client)
      const product = products.find(p => p.id === productId);
      if (product && !returnMode && newQuantity > sellableStock(product)) {
        Swal.fire({
          title: 'Stock insuffisant',
          text: `Stock disponible: ${sellableStock(product)}`,
          icon: 'warning',
          confirmButtonColor: KI.coral
        });
        return;
      }
      
      setSelectedProducts(selectedProducts.map(p => 
        p.id === productId ? { ...p, quantity: newQuantity } : p
      ));
    }
  };

  // Remove product from cart
  const removeFromCart = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  // Add service to selection
  const addService = (service) => {
    if (returnMode) {
      Swal.fire({ title: 'Mode retour', text: 'Les services ne se retournent pas. Quittez le mode retour pour ajouter un service.', icon: 'warning', confirmButtonColor: KI.coralDark });
      return;
    }
    const existing = selectedServices.find(s => s.id === service.id);
    if (existing) {
      // Increase quantity
      setSelectedServices(selectedServices.map(s => 
        s.id === service.id 
          ? { ...s, quantity: (s.quantity || 1) + 1 }
          : s
      ));
    } else {
      // Add new service with current price and discount (ensure numeric values)
      setSelectedServices([...selectedServices, {
        ...service,
        quantity: 1,
        price: parseFloat(service.price) || 0,
        remise: parseFloat(service.remise) || 0
      }]);
    }
  };

  // Update service quantity
  const updateServiceQuantity = (serviceId, newQuantity) => {
    if (newQuantity <= 0) {
      setSelectedServices(selectedServices.filter(s => s.id !== serviceId));
    } else {
      setSelectedServices(selectedServices.map(s => 
        s.id === serviceId ? { ...s, quantity: newQuantity } : s
      ));
    }
  };

  // Remove service from selection
  const removeService = (serviceId) => {
    setSelectedServices(selectedServices.filter(s => s.id !== serviceId));
  };

  // Toggle a service line as "gratuit" (comped/free). Only meaningful for a
  // Passager + ACTE/DIAGNOSTIC line (the button is only shown there); the totals
  // and the sale payload treat such a line as 0 DT while the flag holds.
  const toggleServiceGratuit = (serviceId) => {
    setSelectedServices(selectedServices.map(s =>
      s.id === serviceId ? { ...s, gratuit: !s.gratuit } : s
    ));
  };

  // Toggle a SOIN line as « payé avec points ». Refuses to toggle ON when the
  // remaining balance (after the other toggled soins) can't cover the cost.
  const toggleServicePayWithPoints = (serviceId) => {
    setSelectedServices(prev => prev.map(s => {
      if (s.id !== serviceId) return s;
      if (!s.payWithPoints) {
        const cost = round3((servicePointsCost(s) || 0) * (s.quantity || 1));
        if (pointsBalance - soinsPointsSpend + 1e-6 < cost) {
          Swal.fire({ icon: 'warning', title: 'Points insuffisants', text: `Ce soin coûte ${cost} points — solde disponible : ${round3(pointsBalance - soinsPointsSpend)} points.`, timer: 2600, showConfirmButton: false });
          return s;
        }
      }
      return { ...s, payWithPoints: !s.payWithPoints };
    }));
  };

  // "Montant payé" edited by the cashier. Marks it touched (stops auto-fill) and,
  // if it becomes 0 (client can't pay), removes the remise — no payment, no discount.
  const handleAmountPaidChange = (value) => {
    setAmountPaidRaw(value);
    setAmountPaidTouched(true);
    let v;
    if (value === '' || value === '-') v = 0;
    else { const n = parseFloat(value); v = (!isNaN(n) && n >= 0) ? n : 0; }
    setAmountPaid(v);
    if (v === 0) { setDiscount(0); setPointsToUse(0); }
  };
  // Set "Montant payé" to an exact value from a button (Montant exact / +d / Effacer).
  const setAmountPaidExact = (v) => {
    setAmountPaidTouched(true);
    setAmountPaid(v);
    if (v === 0) { setDiscount(0); setPointsToUse(0); }
  };

  // Start a fresh sale: clears the current one (cancels it) and resets the page to
  // a blank state on the Passager client — one click, no extra dialog.
  const startNewSale = () => {
    setSelectedProducts([]);
    setSelectedServices([]);
    setPendingSessionId(null); // abandoned « Terminer » flow — its session stays open
    setAmountPaid(0);
    setDiscountRaw(null);
    setRecevoirRaw(null);
    setAmountPaidRaw(null);
    setAmountPaidTouched(false);
    setPointsToUse(0);
    setShowPanier(false);
    resetToPassager(); // a new sale always starts on the walk-in client
  };

  // Select customer
  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery('');
    setShowCustomers(false);
    
    // Fetch full customer details with category
    try {
      const customerDetails = await customerService.getCustomerById(customer.id);
      setSelectedCustomer(customerDetails);
      
      // If customer has a category, fetch category details and apply discount
      if (customerDetails.category) {
        try {
          const categoryData = await clientCategoryService.getCategoryById(customerDetails.category);
          setCustomerCategory(categoryData);

          // The category remise flows in via factureRemisePercent → the global
          // remise effect, which seeds the per-line discounts. Nothing to set here.
        } catch (err) {
          console.error('Error fetching category details:', err);
        }
      } else {
        setCustomerCategory(null);
        setDiscount(0);
      }

      // Load rewards summary for non-Passager customers
      if (customerDetails.id !== 5) {
        try {
          const summary = await rewardService.getCustomerSummary(customerDetails.id);
          setRewardsSummary(summary);
          setShowRewardsBubble(true);
          
          // Calculate next feature to unlock (based on points)
          if (summary.category && summary.category.features) {
            const currentCashback = summary.points || 0;
            const features = summary.category.features.filter(f => 
              typeof f === 'object' && f.points > currentCashback
            ).sort((a, b) => a.points - b.points);
            
            if (features.length > 0) {
              setNextFeature(features[0]);
            } else {
              setNextFeature(null);
            }
          }
        } catch (err) {
          console.error('Error loading rewards:', err);
          setShowRewardsBubble(false);
        }
      } else {
        // For Passager (walk-in): the default 5% remise flows in via
        // factureRemisePercent → the global remise effect (seeds each line).
        setShowRewardsBubble(false);
        setRewardsSummary(null);
      }
    } catch (err) {
      console.error('Error fetching customer details:', err);
    }
  };

  // Reset the client back to the default "Passager" (id=5). Used after a sale
  // so the page always starts a new sale on the walk-in customer.
  const resetToPassager = () => {
    // selectCustomer re-fetches full details by id, so a minimal {id:5} is enough
    // when the current customers list (e.g. search results) doesn't include Passager.
    const passager = customers.find(c => c.id === 5) || { id: 5 };
    selectCustomer(passager);
  };

  // Handle adding new customer
  const handleAddCustomer = async () => {
    try {
      if (!newCustomerData.first_name.trim()) {
        Swal.fire('Erreur', 'Le prénom est requis', 'error');
        return;
      }

      // Prepare data with defaults for empty values
      const customerData = {
        ...newCustomerData,
        score: newCustomerData.score === '' || newCustomerData.score === null || newCustomerData.score === undefined ? 0 : newCustomerData.score,
        points: newCustomerData.points === '' || newCustomerData.points === null || newCustomerData.points === undefined ? 0 : newCustomerData.points,
        credit: newCustomerData.credit === '' || newCustomerData.credit === null || newCustomerData.credit === undefined ? 0 : newCustomerData.credit,
        credit_maximum: newCustomerData.credit_maximum === '' || newCustomerData.credit_maximum === null || newCustomerData.credit_maximum === undefined ? 0 : newCustomerData.credit_maximum,
        category_id: newCustomerData.category_id || null
      };

      const createdCustomer = await customerService.createCustomer(customerData);
      setSelectedCustomer(createdCustomer);
      setShowAddCustomerModal(false);
      setNewCustomerData({ 
        first_name: '', 
        last_name: '', 
        phone: '', 
        email: '',
        address: '',
        points: '',
        credit: '',
        credit_maximum: '',
        score: '',
        category_id: ''
      });
      Swal.fire('Succès', 'Client ajouté avec succès', 'success');
    } catch (err) {
      console.error('Error adding customer:', err);
      Swal.fire('Erreur', 'Erreur lors de l\'ajout du client', 'error');
    }
  };

  // ===== Retour client (bouton « Retour » de l'entête du panier) =====
  // Remboursement = total du panier (remise client comprise) ; en mode crédit,
  // la part couverte par le crédit du client en est déduite, le reste en espèces.
  const customerCredit = (selectedCustomer && !isPassagerCustomer()) ? (parseFloat(selectedCustomer.credit) || 0) : 0;
  const refundCreditPart = (returnMode && refundMode === 'credit') ? round3(Math.min(totalAmount, customerCredit)) : 0;
  const refundCash = round3(totalAmount - refundCreditPart);

  // Historique du client (vrai client seulement) : quantité achetée / déjà
  // retournée par produit. Une ligne jamais retarifée prend le PRIX et la
  // REMISE de son dernier achat — on rembourse ce que le client a payé.
  const returnIdsKey = returnMode ? [...new Set(selectedProducts.map((p) => p.id))].sort((a, b) => a - b).join(',') : '';
  const returnCustomerId = (returnMode && selectedCustomer && !isPassagerCustomer()) ? selectedCustomer.id : null;
  useEffect(() => {
    if (!returnCustomerId || !returnIdsKey) {
      setReturnHistory((h) => (Object.keys(h).length ? {} : h));
      return undefined;
    }
    let alive = true;
    salesService.getReturnable(returnCustomerId, returnIdsKey).then((h) => {
      if (!alive) return;
      setReturnHistory(h || {});
      setSelectedProducts((prev) => prev.map((p) => {
        const r = (h || {})[p.id];
        if (!r || !r.bought || r.last_price == null || p.histFor === returnCustomerId || (p.remiseEdited && !p.histFor)) return p;
        return { ...p, price: r.last_price, promo: null, remisePct: r.last_remise_pct || 0, remiseEdited: true, histFor: returnCustomerId };
      }));
    }).catch((e) => console.warn('Historique retour :', e));
    return () => { alive = false; };
  }, [returnCustomerId, returnIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lignes au-delà de ce que le client a acheté (moins ses retours) — avertissement seulement.
  const returnOverLines = returnCustomerId
    ? selectedProducts.filter((p) => { const r = returnHistory[p.id]; return !r || p.quantity > ((r.bought || 0) - (r.returned || 0)); })
    : [];

  const exitReturnMode = () => {
    returnApproval.current = null;
    setReturnMode(false);
    setRefundMode('cash');
    setReturnHistory({});
    // Retour à la vente : prix du jour, et jamais plus que le stock vendable.
    // Les lignes réduites / retirées faute de stock sont signalées (jamais en silence).
    const changed = [];
    const next = selectedProducts.map((p) => {
      const base = products.find((x) => x.id === p.id) || p;
      let line = p;
      if (p.histFor) {
        const promo = promoFor(base);
        line = { ...p, price: promo ? promo.price : (base.price || 0), promo: promo ? promo.name : null, remisePct: promo ? 0 : discount, remiseEdited: false, histFor: null };
      }
      const qty = Math.max(0, Math.min(line.quantity, sellableStock(base)));
      if (qty < line.quantity) changed.push(qty > 0 ? `${p.name} (→ ${qty})` : p.name);
      return { ...line, quantity: qty };
    }).filter((p) => p.quantity > 0);
    setSelectedProducts(next);
    if (changed.length > 0) {
      Swal.fire({
        title: 'Retour quitté',
        text: `Pas assez de stock pour les vendre : ${changed.join(', ')} — retiré(s) ou réduit(s) dans le panier.`,
        icon: 'info', confirmButtonColor: KI.coralDark
      });
    }
  };

  // Entre en mode retour ; false si le panier ne le permet pas (message affiché).
  const enterReturnMode = () => {
    if (returnMode) return true;
    if (selectedServices.length > 0 || pendingReservationId || pendingSessionId) {
      Swal.fire({
        title: 'Retour impossible',
        text: selectedServices.length > 0
          ? 'Les services ne se retournent pas : retirez-les du panier pour faire un retour de produits.'
          : 'Ce panier vient d’une réservation ou d’un rendez-vous : terminez-le ou videz le panier d’abord.',
        icon: 'warning', confirmButtonColor: KI.coralDark
      });
      return false;
    }
    setReturnMode(true);
    setRefundMode('cash');
    setPointsToUse(0);
    setAmountPaid(0);
    return true;
  };

  // Bouton « Retour » de Filtres › Réapprovisionnement (à côté de « Passer une
  // commande ») : entre en mode retour et referme le panneau pour montrer le
  // catalogue — tous les produits, même en « Stock insuffisant ». Un 2e clic
  // (ou « Annuler » du bandeau rouge du panier) revient à la vente.
  const toggleReturnMode = async () => {
    if (returnMode) { exitReturnMode(); return; }
    if (!(await approveReturn())) return;
    if (enterReturnMode()) { setCatalogTab('products'); setShowFilters(false); }
  };

  // Un retour client se fait seulement avec un manager ou le propriétaire
  // (owner 2026-09-28). Compte manager / propriétaire : rien à demander. Compte
  // vendeur : le CODE d'une personne manager / propriétaire, redemandé si
  // l'accord a plus de 4 min (le serveur garde la preuve 5 min).
  const RETURN_APPROVAL_MS = 4 * 60 * 1000;
  const approveReturn = async () => {
    if (authService.isAdmin()) return true;
    const a = returnApproval.current;
    if (a && Date.now() - a.at < RETURN_APPROVAL_MS) return true;
    const who = await requestIdentity({
      minLevel: 'manager',
      title: 'Retour client — accord d’un manager',
      reason: 'Un retour se fait seulement avec un manager ou le propriétaire.'
    });
    returnApproval.current = who ? { who, at: Date.now() } : null;
    return !!who;
  };


  // Sous-ligne « historique » d'une ligne du panier en mode retour (vrai client).
  const returnHint = (p) => {
    if (!returnCustomerId) return null;
    const r = returnHistory[p.id];
    if (!r) return null;
    if (!r.bought) return <p className="text-[11px] text-amber-700 mt-1">Jamais acheté par ce client</p>;
    const left = (r.bought || 0) - (r.returned || 0);
    const d = r.last_date ? new Date(r.last_date).toLocaleDateString('fr-FR') : '';
    return (
      <p className={`text-[11px] mt-1 ${p.quantity > left ? 'text-amber-700' : 'text-gray-500'}`}>
        Acheté {r.bought}{r.returned ? ` · déjà retourné ${r.returned}` : ''}{d ? ` · dernier achat ${d}` : ''}{r.last_remise_pct > 0 ? ` (−${r.last_remise_pct}%)` : ''}
        {p.quantity > left && ' · plus que acheté'}
      </p>
    );
  };

  // Choix du remboursement : espèces, ou déduit d'abord du crédit du client.
  const refundChooser = (
    <div>
      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-200">
        {[['cash', 'Espèces'], ['credit', 'Déduire du crédit']].map(([k, label]) => {
          const off = k === 'credit' && customerCredit <= 0;
          return (
            <button key={k} type="button" disabled={off} onClick={() => setRefundMode(k)} aria-pressed={refundMode === k}
              title={off ? 'Ce client n’a pas de crédit en cours' : ''}
              className={`py-2 rounded-lg text-xs transition-colors ${refundMode === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'} ${off ? 'opacity-45 cursor-not-allowed' : ''}`}>
              {label}
            </button>
          );
        })}
      </div>
      {customerCredit > 0 && <span className="block text-[10px] text-gray-500 mt-1">Crédit en cours : {customerCredit.toFixed(3)} DT</span>}
    </div>
  );

  const escHtml = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const handleSubmitReturn = async () => {
    if (selectedProducts.length === 0) {
      Swal.fire({ title: 'Panier vide', text: 'Ajoutez les produits que le client rapporte.', icon: 'warning', confirmButtonColor: KI.coralDark });
      return;
    }
    if (selectedServices.length > 0 || !selectedCustomer) return;

    const who = isPassagerCustomer() ? 'Passager' : (`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || 'Client');
    const lines = selectedProducts.map((p) => `<li>${p.quantity} × ${escHtml(p.name)}</li>`).join('');
    const warn = returnOverLines.length > 0
      ? `<p style="margin-top:10px;color:${KI.goldText}"><b>Attention :</b> ${returnOverLines.map((p) => escHtml(p.name)).join(', ')} — plus que ce que ce client a acheté (ou jamais acheté par lui).</p>`
      : '';
    const money = (refundCreditPart > 0 ? `Déduit du crédit : <b>${refundCreditPart.toFixed(3)} DT</b><br/>` : '')
      + `À rendre en espèces : <b>${refundCash.toFixed(3)} DT</b>`;
    const ok = await Swal.fire({
      title: 'Valider le retour ?',
      html: `<div style="text-align:left;font-size:14px">Client : <b>${escHtml(who)}</b>`
        + `<ul style="margin:8px 0 8px 18px;list-style:disc">${lines}</ul>`
        + `Total remboursé : <b>${totalAmount.toFixed(3)} DT</b><br/>${money}`
        + `<p style="margin-top:8px;color:${KI.muted}">Ces produits reviennent en stock${isPassagerCustomer() ? '' : ' et les points qu’ils avaient rapportés sont repris'}.</p>${warn}</div>`,
      showCancelButton: true,
      confirmButtonText: 'Valider le retour',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.coralDark,
      reverseButtons: true
    });
    if (!ok.isConfirmed) return;

    let seller;
    if (authService.isAdmin()) {
      seller = await requestIdentity({ title: 'Qui fait le retour ?' });
      if (seller === false) return;
    } else {
      if (!(await approveReturn())) return;
      seller = returnApproval.current.who;
    }

    try {
      setSubmitting(true);
      const saved = await withIdentity(seller, () => salesService.createSale({
        ...actorFields(seller),
        client: selectedCustomer.id,
        is_return: true,
        refund_mode: refundCreditPart > 0 ? 'credit' : 'cash',
        products: selectedProducts.map((p) => ({ product: p.id, quantity: p.quantity, price: p.price })),
        services: [],
        discount: round3(totalRemise),
        is_discount_percent: false,
        amount_paid: 0,
        points_pay: 0,
        reservation_ids: [],
        session_ids: []
      }));

      // Chiffres du serveur quand il a répondu (sinon : file hors ligne → ceux de l'écran).
      const cash = saved && saved.amount_payed != null ? Math.abs(parseFloat(saved.amount_payed) || 0) : refundCash;
      const offset = saved && saved.return_credit_offset != null ? (parseFloat(saved.return_credit_offset) || 0) : refundCreditPart;

      // Poste de bureau : le tiroir s'ouvre pour rendre les espèces.
      if (typeof window !== 'undefined' && window.kipara && cash > 0) {
        window.kipara.getPeripherals()
          .then((cfg) => { if (cfg && cfg.openDrawerOnCash) return window.kipara.openDrawer(); return null; })
          .catch((e) => console.warn('Tiroir-caisse :', e));
      }

      setSelectedProducts([]);
      returnApproval.current = null;
      setReturnMode(false);
      setRefundMode('cash');
      setReturnHistory({});
      setAmountPaid(0);
      resetToPassager();
      setShowPanier(false);
      await loadProducts();
      loadBlockedReservations();

      Swal.fire({
        title: 'Retour enregistré',
        html: (offset > 0 ? `Déduit du crédit du client : <b>${offset.toFixed(3)} DT</b><br/>` : '')
          + `À rendre au client : <b>${cash.toFixed(3)} DT</b>`,
        icon: 'success',
        confirmButtonColor: KI.coralDark
      });
    } catch (err) {
      console.error('Error creating return:', err);
      returnApproval.current = null; // refus (accord expiré…) : le code sera redemandé
      const backendMsg = err?.error || err?.detail || err?.response?.data?.error || err?.response?.data?.detail;
      Swal.fire({ title: 'Erreur', text: backendMsg || 'Erreur lors de l’enregistrement du retour', icon: 'error', confirmButtonColor: KI.coralDark });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit sale
  const handleSubmitSale = async () => {
    if (selectedProducts.length === 0 && selectedServices.length === 0) {
      Swal.fire('Erreur', 'Veuillez ajouter au moins un produit ou service', 'error');
      return;
    }

    if (!selectedCustomer) {
      Swal.fire('Erreur', 'Veuillez sélectionner un client', 'error');
      return;
    }

    // Business rule: a SOIN service can only be sold against an OPEN rendez-vous
    // of this client (not cancelled, not already encaissé, not paid at booking).
    // The backend checks it AND closes that rendez-vous (Terminé + Payé) in the
    // sale's transaction; its refusal message is shown by the catch below.

    // Plafond de crédit : si la vente laisse un impayé et que le client a un
    // credit_maximum (> 0 = plafond actif, 0 = pas de limite), bloquer quand
    // solde actuel + nouvel impayé dépasse le plafond. Le backend refait la
    // même vérification de façon autoritaire (rollback complet de la vente).
    const venteCredit = round3(Math.max(0, totalAmount - (amountPaid + pointsPay + avancePay)));
    const plafondCredit = parseFloat(selectedCustomer.credit_maximum) || 0;
    if (venteCredit > 0 && plafondCredit > 0) {
      const soldeActuel = parseFloat(selectedCustomer.credit) || 0;
      if (soldeActuel + venteCredit > plafondCredit + 1e-6) {
        Swal.fire({
          title: 'Plafond de crédit dépassé',
          html: `Reste à payer actuel : <b>${soldeActuel.toFixed(3)} DT</b><br/>` +
                `Crédit de cette vente : <b>${venteCredit.toFixed(3)} DT</b><br/>` +
                `Maximum autorisé : <b>${plafondCredit.toFixed(3)} DT</b><br/><br/>` +
                `Encaissez au moins <b>${(soldeActuel + venteCredit - plafondCredit).toFixed(3)} DT</b> de plus pour valider la vente.`,
          icon: 'warning',
          confirmButtonColor: KI.coralDark
        });
        return;
      }
    }

    // Soins « payés avec points » : le solde doit couvrir le coût total (la
    // quantité a pu augmenter après l'activation du toggle).
    if (soinsPointsSpend > pointsBalance + 1e-6) {
      Swal.fire({
        title: 'Points insuffisants',
        text: `Les soins payés avec points coûtent ${soinsPointsSpend} points, mais le client n'en a que ${pointsBalance}.`,
        icon: 'warning', confirmButtonColor: KI.coralDark
      });
      return;
    }

    // Qui encaisse ? Le vendeur s'identifie (code PIN) une fois le
    // panier validé ; son id part avec la vente. null = identification pas
    // encore configurée (aucun PIN) -> compte connecté ; false = annulé.
    const seller = await requestIdentity({ title: 'Qui encaisse ?' });
    if (seller === false) return;

    try {
      setSubmitting(true);

      const saleData = {
        ...actorFields(seller),
        client: selectedCustomer.id,
        products: selectedProducts.map(product => ({
          product: product.id,
          quantity: product.quantity,
          price: product.price
        })),
        services: selectedServices.map(service => {
          // "Gratuit" (comped) OR « payé avec points » service → recorded at
          // 0 DT (the points cost travels via points_pay, not the price).
          const freeLine = isServiceGratuit(service) || isServicePaidWithPoints(service);
          return {
            service: service.id,
            quantity: service.quantity || 1,
            price: freeLine ? 0 : service.price,
            remise: freeLine ? 0 : (service.remise || 0)
          };
        }),
        // Per-line product remises + global remise → one absolute discount_amount.
        discount: round3(totalRemise),
        is_discount_percent: false,
        // Cash + points + avance réservation act as the payment tender (the
        // avance was received at reservation time — it finally enters the sale
        // and the encaissé here), so the client isn't recorded as owing them.
        // Le trop-perçu d'avance part aussi : le backend le range en monnaie
        // rendue (amount_change).
        amount_paid: round3(amountPaid + pointsPay + avancePay + avanceSurplus),
        // Points spent as tender — the backend deducts them from the cashback
        // balance in the same transaction that credits the points earned.
        // Gold 1 pt = 1 DT tender + the category-cost of soins « payés avec
        // points » (their lines are sent at 0 DT above).
        points_pay: round3(pointsPay + soinsPointsSpend),
        // Réservations clôturées par cette vente, dans la MÊME transaction
        // côté backend : celle du flux « Vendre au client » + les réservations
        // bloquées de ce client que le panier consomme.
        reservation_ids: [...new Set([
          ...(pendingReservationId ? [pendingReservationId] : []),
          ...consumedBlockedReservations.flatMap((b) => b.ids),
        ])],
        // Session « Terminer » (Sessions du jour / Réservation) : le backend la
        // passe Terminé + Payé avec la vente. Sans id (soin ajouté à la main),
        // il retrouve le rendez-vous ouvert du client — même résultat.
        session_ids: pendingSessionId ? [pendingSessionId] : []
      };

      console.log('=== Sale Created Successfully ===');
      await withIdentity(seller, () => salesService.createSale(saleData));

      // Desktop app: open the cash drawer after a cash sale when this PC is
      // configured for it (page « Ce poste »). Best-effort, never blocks.
      if (typeof window !== 'undefined' && window.kipara && amountPaid > 0) {
        window.kipara.getPeripherals()
          .then((cfg) => { if (cfg && cfg.openDrawerOnCash) return window.kipara.openDrawer(); return null; })
          .catch((e) => console.warn('Tiroir-caisse :', e));
      }

      if (pendingReservationId) {
        setPendingReservationId(null);
        setReservationAvance(0);
      }

      // La session « Terminer » a été clôturée côté serveur avec la vente ; on
      // recharge les rendez-vous pour que le soin encaissé quitte le catalogue.
      if (pendingSessionId) setPendingSessionId(null);
      if (selectedServices.some((s) => s.type === 'SOIN')) loadSessions();

      // Update customer score and cashback points based on category
      let categoryUpgraded = false;
      let newCategory = null;
      let newRewardEarned = null;
      
      console.log('=== Starting Customer Update Process ===');
      console.log('Customer Category:', customerCategory);
      console.log('Selected Customer ID:', selectedCustomer.id);
      console.log('Is Passager?:', selectedCustomer.id === 5);
      
      if (customerCategory && selectedCustomer.id !== 5) {
        try {
          const currentScore = parseFloat(selectedCustomer.score || 0);
          const currentCashback = parseFloat(selectedCustomer.points || 0);
          
          // Points earned on the gross product total (before remise) — same base as
          // the panier/card display (calculatePointsGain), so what's shown = credited.
          const scoreToAdd = calculatePointsGain();
          const cashbackToAdd = calculatePointsGain();
          const newScore = currentScore + scoreToAdd;
          // Spend points used as payment (1 pt = 1 DT) from the cashback balance.
          // Score is permanent and is NOT reduced by spending points.
          const newCashbackPoints = Math.max(0, currentCashback + cashbackToAdd - pointsPay - soinsPointsSpend);
          
          console.log('=== Customer Update Details ===');
          console.log('Customer ID:', selectedCustomer.id);
          console.log('Current Score:', currentScore);
          console.log('Score to Add:', scoreToAdd);
          console.log('New Score (calculated):', newScore);
          console.log('New Score (rounded):', Math.round(newScore));
          console.log('Current Cashback:', currentCashback);
          console.log('Cashback to Add:', cashbackToAdd);
          console.log('New Cashback:', newCashbackPoints.toFixed(2));
          console.log('Total Amount:', totalAmount);
          console.log('Cashback Value %:', customerCategory.points_value);
          
          // Check if customer qualifies for category upgrade
          // Use rounded score for upgrade check to match what will be saved
          const roundedNewScore = Math.round(newScore);
          const upgradeCategory = checkCategoryUpgrade(Math.round(currentScore), roundedNewScore);
          
          if (upgradeCategory) {
            categoryUpgraded = true;
            newCategory = upgradeCategory;
            console.log('Category Upgrade:', customerCategory.name, '->', upgradeCategory.name);
          }
          
          // NO client-side write anymore: the backend credits score/points
          // (and applies the tier upgrade + points_pay deduction) inside the
          // sale-creation transaction, for EVERY channel. Writing here again
          // would double-count or overwrite the server's values. The numbers
          // computed above are kept only to drive the UI (upgrade / reward
          // modals shown below).
          
          // Check if customer can now claim new features (reached cashback threshold)
          if (customerCategory && customerCategory.features) {
            console.log('Checking for claimable features...');
            console.log('Current cashback:', currentCashback, 'New cashback:', newCashbackPoints);
            console.log('Features:', customerCategory.features);
            
            const newlyClaimableFeatures = customerCategory.features.filter(f => {
              if (typeof f === 'object' && f.points) {
                // Check if customer just reached this threshold
                const justUnlocked = currentCashback < f.points && newCashbackPoints >= f.points;
                if (justUnlocked) {
                  console.log(`Feature "${f.feature}" now claimable! (${f.points} cashback pts)`);
                }
                return justUnlocked;
              }
              return false;
            });
            
            console.log('Newly claimable features count:', newlyClaimableFeatures.length);
            
            // Show alert for newly claimable features (don't auto-create reward)
            if (newlyClaimableFeatures.length > 0) {
              newRewardEarned = newlyClaimableFeatures[0];
              setEarnedReward(newlyClaimableFeatures[0]);
              setEarnedRewardCustomerId(selectedCustomer.id);
              console.log('✨ Showing claimable feature alert:', newlyClaimableFeatures[0]);
            }
          }
          
        } catch (err) {
          console.error('Error updating customer score/cashback:', err);
          console.error('Error details:', err.response?.data || err.message);
          // Don't fail the sale if this fails, just log it
        }
      }
      
      // Reload products to refresh stock information
      await loadProducts();
      loadBlockedReservations();
      
      // Save customer name before reset for modals
      const customerName = selectedCustomer.first_name + ' ' + (selectedCustomer.last_name || '');
      const savedCustomerCategory = customerCategory;
      
      // Show upgrade modal if category was upgraded
      if (categoryUpgraded && newCategory) {
        setUpgradeInfo({
          customerName: customerName,
          oldCategory: savedCustomerCategory,
          newCategory: newCategory,
          pointsGained: calculatePointsGain()
        });
        setShowUpgradeModal(true);
        
        // Reset form after showing modal
        setSelectedProducts([]);
        setSelectedServices([]);
        setAmountPaid(0);
        setShowRewardsBubble(false);
        setRewardsSummary(null);
        resetToPassager(); // default client back to Passager for the next sale
        setShowPanier(false); // close the panier popup after the sale
      } 
      // Show reward earned modal if reward was unlocked (but not category upgrade)
      else if (newRewardEarned) {
        console.log('Showing reward earned modal for:', newRewardEarned);
        setShowRewardEarnedModal(true);
        
        // Reset form after showing modal
        setSelectedProducts([]);
        setSelectedServices([]);
        setAmountPaid(0);
        setShowRewardsBubble(false);
        setRewardsSummary(null);
        resetToPassager(); // default client back to Passager for the next sale
        setShowPanier(false); // close the panier popup after the sale
      } 
      // Otherwise just reset (no « Succès » popup — owner 2026-10-03 : it had no use)
      else {
        // Reset form
        setSelectedProducts([]);
        setSelectedServices([]);
        setAmountPaid(0);
        setShowRewardsBubble(false);
        setRewardsSummary(null);
        resetToPassager(); // default client back to Passager for the next sale
        setShowPanier(false); // close the panier popup after the sale
      }
      
    } catch (err) {
      console.error('Error creating sale:', err);
      // salesService throws the backend JSON ({ error } / { detail }) directly.
      const backendMsg = err?.error || err?.detail || err?.response?.data?.error;
      Swal.fire({
        title: 'Erreur',
        text: backendMsg || 'Erreur lors de la création de la vente',
        icon: 'error',
        confirmButtonColor: KI.coralDark
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Open confirmation dialog for reward
  const handleClaimReward = (feature) => {
    if (!rewardsSummary || !feature) return;
    
    // Check if customer has enough cashback
    if (rewardsSummary.points < feature.points) {
      Swal.fire({
        title: 'Points insuffisants',
        text: `Vous avez besoin de ${feature.points} points pour utiliser cette récompense.`,
        icon: 'warning',
        confirmButtonColor: KI.coralDark
      });
      return;
    }
    
    // Open confirmation dialog
    setRewardToConfirm(feature);
    setConfirmRewardDialog(true);
  };

  // Confirm and execute reward claim
  const confirmClaimReward = async () => {
    if (!rewardsSummary || !rewardToConfirm) return;
    
    try {
      // Débiter UNIQUEMENT les points. Renvoyer la fiche entière réécrivait
      // aussi le crédit avec une valeur déjà périmée, ce qui effaçait la dette
      // du client (et, côté serveur, réécrivait ses ventes).
      const customerData = await customerService.getCustomerById(rewardsSummary.customer_id);
      const currentPoints = Number(customerData.points) || 0;
      await customerService.patchCustomer(rewardsSummary.customer_id, {
        points: Math.max(0, currentPoints - rewardToConfirm.points),
      });
      
      // Refresh the rewards summary
      const updatedSummary = await rewardService.getCustomerSummary(rewardsSummary.customer_id);
      setRewardsSummary(updatedSummary);
      
      // Close dialog
      setConfirmRewardDialog(false);
      setRewardToConfirm(null);
      
      Swal.fire({
        title: 'Récompense utilisée!',
        html: `<p class="mb-2">${rewardToConfirm.feature}</p><p class="text-sm text-gray-600">Nouveau solde: <strong>${(rewardsSummary.points - rewardToConfirm.points).toFixed(2)} points</strong></p>`,
        icon: 'success',
        confirmButtonColor: KI.spruce,
        timer: 3000
      });
      
    } catch (err) {
      console.error('Error claiming reward:', err);
      setConfirmRewardDialog(false);
      setRewardToConfirm(null);
      Swal.fire({
        title: 'Erreur',
        text: 'Impossible d\'utiliser cette récompense',
        icon: 'error',
        confirmButtonColor: KI.coralDark
      });
    }
  };

  // Handle using a reward
  const handleUseReward = async () => {
    if (!rewardToUse) return;
    
    try {
      await rewardService.useReward(rewardToUse.id);
      
      // Refresh rewards summary
      if (selectedCustomer) {
        const summary = await rewardService.getCustomerSummary(selectedCustomer.id);
        setRewardsSummary(summary);
      }
      
      setUseRewardDialogOpen(false);
      setRewardToUse(null);
      
      Swal.fire({
        title: 'Récompense utilisée!',
        text: 'La récompense a été marquée comme utilisée',
        icon: 'success',
        confirmButtonColor: KI.coralDark,
        timer: 2000
      });
    } catch (err) {
      console.error('Error using reward:', err);
      Swal.fire({
        title: 'Erreur',
        text: 'Impossible d\'utiliser cette récompense',
        icon: 'error',
        confirmButtonColor: KI.coralDark
      });
    }
  };

  // Page scrolls globally; the product/catalogue list keeps its own bounded inner scroll.

  return (
    /* Même enveloppe que /reservations : la page EST le bloc
       `w-full px-4 py-5`, sans conteneur intermédiaire. Les deux ancêtres
       précédents (`min-h-screen bg-transparent md:bg-gray-100` + un <div> nu)
       ont été retirés parce qu'ils cassaient la navigation entre les deux
       pages (owner 2026-09-07) :
         · `min-h-screen` DANS <main> (déjà `min-h-screen` + p-6 + footer)
           forçait une barre de défilement en permanence ici et jamais sur
           /reservations -> le layout sautait horizontalement à chaque aller-retour ;
         · le <div> nu décalait d'un cran la cible de `.kp-page > * > *`
           (index.css §2) : /reservations animait ses 3 blocs, /seller-sales
           animait un seul bloc contenant AUSSI les modales.
       Le fond `md:bg-gray-100` valait #F3F4F6, exactement le `bg-light` du
       shell : sa suppression ne change aucune couleur. */
    <>
      <div className="w-full px-4 pt-2 pb-4">

          {/* Sub-nav row: tabs + (owner request) Produits/Services toggle on the
              same line. Page title + red icon box removed per owner Studio. */}
          <VenteTabs>
            {/* reset button removed per owner Studio */}
            {/* Même bascule que Soin / Produits de /reservations : kit « pilule »
                .segp, et l'icône Produits passe à IcPackageOpen comme là-bas
                (owner 2026-09-06). */}
            <div className="segp">
              <button
                type="button"
                onClick={() => setCatalogTab('products')}
                className={catalogTab === 'products' ? 'segp-on' : 'segp-off'}
              >
                <IcPackageOpen size={13} /> Produits
              </button>
              <button
                type="button"
                onClick={() => setCatalogTab('services')}
                className={catalogTab === 'services' ? 'segp-on' : 'segp-off'}
              >
                <IcSparkles size={13} /> Services
              </button>
            </div>
          </VenteTabs>

          {/* Toolbar: search + filtres. Mobile (Ahmed 2026-08-17) : la barre de
              recherche reste toujours visible mais sans autofocus — le clavier
              ne s'ouvre que quand on touche le champ. */}
          <div className="flex gap-3 mb-3 flex-shrink-0">
            <div className="relative flex-1 min-w-0">
              {/* Composants-2 §2 : 44px, icône scan-barcode 17px #6B7280 à
                  13px du bord, texte à 9px de l'icône (pl 39px). */}
              {catalogTab === 'products' ? (
                <IcScanBarcode className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
              ) : (
                <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
              )}
              {catalogTab === 'products' ? (
                <input
                  type="text"
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  onKeyDown={handleProductSearchEnter}
                  placeholder="Rechercher ou scanner un code-barres, puis Entrée…"
                  className="inp h-12 w-full pl-11 pr-10"
                />
              ) : (
                <input
                  type="text"
                  value={serviceSearchQuery}
                  onChange={(e) => setServiceSearchQuery(e.target.value)}
                  placeholder="Rechercher un service..."
                  className="inp h-12 w-full pl-11 pr-10"
                />
              )}
              {(catalogTab === 'products' ? productSearchQuery : serviceSearchQuery) && (
                <button
                  type="button"
                  onClick={() => { setProductSearchQuery(''); setServiceSearchQuery(''); }}
                  aria-label="Effacer la recherche"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  <IcClose />
                </button>
              )}
            </div>
            {catalogTab === 'products' && (
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
              >
                <IcFilters className="text-sm" /> Filtres
              </button>
            )}
          </div>

          {/* Filter panel (products): categorie / sous-categorie / laboratoire
              — Brand Kit §05 : carte bordée #E5E7EB rayon 6 padding 12, grille
              gap 9, libellés 11px gris, selects 38px bordés #D1D5DB (valeur
              « Toutes » en #9CA3AF). */}
          {catalogTab === 'products' && showFilters && (
            <div className="flt-card rounded-2xl mb-4 p-4 flex-shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="flt-label text-xs mb-1.5">Catégorie</label>
                  <div className="flt-sel">
                    <select
                      value={selectedCategoryFilter}
                      onChange={(e) => { setSelectedCategoryFilter(e.target.value); setSelectedSubcategoryFilter(''); }}
                      className={`flt-select pl-3 py-2.5${selectedCategoryFilter ? '' : ' text-gray-400'}`}
                    >
                      <option value="">Toutes les catégories</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="flt-label text-xs mb-1.5">Sous-catégorie</label>
                  <div className="flt-sel">
                    <select
                      value={selectedSubcategoryFilter}
                      onChange={(e) => setSelectedSubcategoryFilter(e.target.value)}
                      disabled={!selectedCategoryFilter}
                      className={`flt-select pl-3 py-2.5${selectedSubcategoryFilter ? '' : ' text-gray-400'}`}
                    >
                      <option value="">Toutes les sous-catégories</option>
                      {filteredSubcategories.map((sub) => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="flt-label text-xs mb-1.5">Laboratoire</label>
                  <div className="flt-sel">
                    <select
                      value={labFilter}
                      onChange={(e) => setLabFilter(e.target.value)}
                      className={`flt-select pl-3 py-2.5${labFilter ? '' : ' text-gray-400'}`}
                    >
                      <option value="">Tous les laboratoires</option>
                      {laboratoires.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                </div>
                {/* 4th rectangle « Réapprovisionnement » : UNE case à choix, comme
                    Catégorie / Sous-catégorie / Laboratoire (owner 2026-09-28) —
                    « Passer une commande » ouvre le popup des produits en rupture ;
                    « Retour client » passe Nouvelle vente en mode retour (tous les
                    produits, même en stock insuffisant). Deux choix seulement. */}
                <div>
                  <label className="flt-label text-xs mb-1.5">Réapprovisionnement</label>
                  <div className="flt-sel">
                    <select
                      value={returnMode ? 'retour' : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'commande') setShowOrderModal(true);
                        else if (v === 'retour' && !returnMode) toggleReturnMode();
                      }}
                      className={`flt-select pl-3 py-2.5${returnMode ? '' : ' text-gray-400'}`}
                    >
                      {/* La vente normale est le mode par défaut : pas d'option
                          pour elle (owner 2026-09-28). On quitte le retour par
                          « Annuler » du bandeau rouge du panier. */}
                      <option value="" disabled hidden>Choisir…</option>
                      <option value="commande">
                        Passer une commande{outOfStockProducts.length > 0 ? ` (${outOfStockProducts.length})` : ''}
                      </option>
                      <option value="retour">Retour client</option>
                    </select>
                  </div>
                </div>
              </div>
              {(selectedCategoryFilter || selectedSubcategoryFilter || labFilter) && (
                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={() => { setSelectedCategoryFilter(''); setSelectedSubcategoryFilter(''); setLabFilter(''); }}
                    className="link-reset text-sm px-3 py-1.5"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Click a product photo → preview popup. Closes on click-outside or when the
              cursor leaves the card. Portalled to body (catalogue uses transforms). */}
          {previewProduct && createPortal(
            (() => {
              const catName = typeof previewProduct.category === 'object' ? previewProduct.category?.name : previewProduct.category_name;
              const subName = typeof previewProduct.subcategory === 'object' ? previewProduct.subcategory?.name : previewProduct.subcategory_name;
              const labName = typeof previewProduct.laboratoire === 'object' ? previewProduct.laboratoire?.name : previewProduct.laboratoire_name;
              /* Disposition owner 2026-09-07 :
                   · labo        → en haut à GAUCHE, posé sur la photo (miroir du bouton fermer) ;
                   · catégorie   → sous la photo, à GAUCHE ;
                   · sous-catég. → sous la photo, à DROITE ;
                   · prix        → aligné à DROITE (était à gauche).
                 L'icône labo est celle du kit (IcFlask, « Laboratoires ») — plus d'emoji. */
              return (
                <div className="modal-backdrop z-[80] flex items-center justify-center p-4" onClick={() => setPreviewProduct(null)}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()} onMouseLeave={() => setPreviewProduct(null)}>
                    <div className="relative h-64 bg-white flex items-center justify-center p-4 border-b border-gray-100">
                      {previewProduct.image
                        ? <img src={previewProduct.image} alt="" className="max-h-full max-w-full object-contain" />
                        : <IcPackage size={72} className="text-gray-200" />}
                      {/* Les 3 étiquettes vivent SUR la photo (owner 2026-09-07, 2e passe) :
                          labo en haut à gauche, catégorie en bas à gauche, sous-catégorie
                          en bas à droite. Fond blanc 90 % pour rester lisibles sur l'image. */}
                      {labName && (
                        <span className="bdg bdg-outline absolute top-2 left-2 inline-flex items-center gap-1 bg-white/90">
                          <IcFlask size={11} /> {labName}
                        </span>
                      )}
                      {/* Owner 2026-09-07 (3e passe) : sous-catégorie en bas à GAUCHE, catégorie en bas
                          à DROITE. Sous-catégorie = bdg-neutral (grammaire des étiquettes, index.css). */}
                      {subName && <span className="bdg bdg-neutral absolute bottom-2 left-2">{subName}</span>}
                      {catName && <span className="bdg bdg-cat absolute bottom-2 right-2">{catName}</span>}
                      <button onClick={() => setPreviewProduct(null)} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center"><IcClose size={13} /></button>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 text-lg leading-tight">{previewProduct.name}</h3>
                      {/* Stock entre le nom et la description (owner 2026-09-07) — MÊME étiquette
                          que la liste des produits (ProductList.jsx) : famille stock du kit,
                          bdg-rupture / bdg-stock-low / bdg-stock-ok, mêmes libellés. */}
                      {(() => {
                        const stockVal = typeof previewProduct.stock === 'number' ? previewProduct.stock : 0;
                        const isOut = stockVal <= 0;
                        const isLow = !isOut && stockVal <= (Number(previewProduct.stock_min) || 0);
                        const stockPill = isOut ? 'bdg-rupture' : isLow ? 'bdg-stock-low' : 'bdg-stock-ok';
                        return (
                          <div className="mt-1.5">
                            <span className={`bdg bdg-td text-xs px-2.5 py-[3px] ${stockPill}`}>
                              {isOut ? 'Rupture' : isLow ? `${stockVal} Stock bas` : `${stockVal} En Stock`}
                            </span>
                          </div>
                        );
                      })()}
                      <p className="text-sm text-gray-600 mt-3 whitespace-pre-line">{previewProduct.description || 'Aucune description'}</p>
                      <p className="text-xl font-extrabold text-black mt-3 text-right tabular-nums">{Number(previewProduct.price).toFixed(3)} <span className="text-sm text-gray-400">DT</span></p>
                    </div>
                  </div>
                </div>
              );
            })(),
            document.body
          )}

          {/* ===== Espace de travail + panneau transaction =====
              Même architecture que /reservations (SoinsCalendarView) : une carte
              principale à gauche, un panneau collant à droite, tous deux SOUS la
              barre d'outils — qui, elle, ne bouge pas (owner 2026-09-07).
              Le rail « Sélection épinglée » a disparu : le panneau liste les
              articles avec leurs quantités, il faisait donc double emploi. */}
          <div className="kp-split">

            {/* ---------- Carte espace produits ---------- */}
            <div className="kp-pane bg-white border border-gray-200 rounded-2xl p-4">
          {/* Catalogue grid (scrollable) */}
          <div className="kp-scroll max-h-[60vh] lg:max-h-none pr-1 -mr-1 lg:pr-0 lg:mr-0">
            {catalogTab === 'products' ? (
              <>
                {/* Packs promo — click to add the whole bundle at its pack price */}
                {packs.length > 0 && (
                  <div className="mb-4">
                    <div className="flex gap-3 overflow-x-auto pb-2 pt-2">
                      {packs.map((pack) => (
                        <button key={pack.id} type="button" onClick={() => addPackToCart(pack)} className="kp-lift shrink-0 w-44 text-left bg-white border-[1.5px] border-amber-500 rounded-2xl p-3 hover:shadow-md hover:-translate-y-0.5 transition duration-200">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="h-8 w-8 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0"><IcBoxes size={13} /></span>
                            <span className="text-sm font-bold text-gray-800 truncate">{pack.name}</span>
                          </div>
                          <p className="text-[11px] text-gray-500 mb-1">{pack.items?.length || 0} produits</p>
                          <div className="flex items-end gap-2">
                            <span className="text-lg font-extrabold text-black leading-none">{Number(pack.promo_price).toFixed(3)}</span>
                            {pack.original_total > pack.promo_price && <span className="text-[11px] text-gray-400 line-through">{Number(pack.original_total).toFixed(3)}</span>}
                          </div>
                          <span className="mt-2 block text-center text-xs bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg py-1.5">+ Ajouter le pack</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 pt-2">
                  {visibleProducts.map((product) => {
                    const isLowStock = product.stock_min && product.stock <= product.stock_min;
                    // Stock levels per owner: two levels only — green when in stock,
                    // orange when low (<= stock_min, same source as the « Stock faible » table).
                    const stockLevel = isLowStock ? 'low' : 'ok';
                    /* Brand Kit (livre, page principale) §05 « Carte produit —
                       caisse » : AUCUN badge stock sur la carte ; « ⚠ faible »
                       en rouge à côté du prix ; + Ajouter vert #15803D (survol
                       #14532D), vert foncé #14532D quand le stock est faible ;
                       désactivé si rupture. */
                    const stockCount = sellableStock(product);
                    const addBtnCls = stockLevel === 'low' ? 'bg-green-900' : '';
                    const inCart = selectedProducts.find((p) => p.id === product.id);
                    const catName = typeof product.category === 'object' ? product.category?.name : null;
                    const promo = promoFor(product);
                    return (
                      <div
                        key={product.id}
                        className="kp-lift group relative flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-xl hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <div
                          className="relative h-32 bg-white overflow-hidden cursor-pointer"
                          onClick={() => setPreviewProduct(product)}
                          title="Voir le produit"
                        >
                          {product.image ? (
                            <img src={product.image} alt={product.name} loading="lazy" decoding="async" className="kp-lift h-full w-full object-contain p-2 group-hover:scale-[1.02] transition-transform duration-200" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-gray-300"><IcPackage size={38} /></div>
                          )}
                          {clientBlockedBonus[product.id] && (
                            <span className="bdg bdg-ink absolute bottom-2 right-2 shadow-sm">
                              <IcLock size={12} /> Bloqué pour ce client
                            </span>
                          )}
                          {inCart && (
                            <div className="bdg-count-ink top-2 left-2 right-auto shadow-md ring-2 ring-white">{inCart.quantity}</div>
                          )}
                        </div>
                        <div className="flex flex-col flex-grow p-3">
                          {catName && <div className="text-[11px] text-gray-400 truncate">{catName}</div>}
                          <h4 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2 min-h-[2.5rem] mb-2 mt-0.5">{product.name}</h4>
                          <div className="mt-auto flex items-end justify-between mb-2">
                            {promo ? (
                              <div className="leading-none">
                                <span className="bdg bdg-promo mb-0.5">PROMO</span>
                                <p className="text-base font-bold tabular-nums text-gray-900 leading-none">{promo.price.toFixed(3)}<span className="text-[11px] font-medium text-gray-500 ml-1">DT</span></p>
                                <p className="text-[11px] text-gray-400 line-through">{product.price?.toFixed(3) || '0.000'}</p>
                              </div>
                            ) : (
                              <p className="text-base font-bold tabular-nums text-gray-900 leading-none">{product.price?.toFixed(3) || '0.000'}<span className="text-[11px] font-medium text-gray-500 ml-1">DT</span></p>
                            )}
                            {stockLevel !== 'ok' && (<span className="text-[10.5px] font-semibold text-red-600">⚠ faible</span>)}
                          </div>
                          <button
                            onClick={() => addToCart(product)}
                            disabled={!returnMode && stockCount <= 0}
                            className={`btn-p kp-press w-full h-9 px-3 gap-1.5 rounded-xl text-[12.5px] transition-all ${addBtnCls}`}
                          >
                            <IcPlus size={10} />
                            {returnMode ? (inCart ? 'Retourner encore' : 'Ajouter au retour') : (inCart ? 'Ajouter encore' : 'Ajouter')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {gridLimit < filteredProducts.length && <div ref={gridEndRef} className="h-px w-full" aria-hidden="true" />}
                {filteredProducts.length === 0 && (
                  <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center mt-2">
                    <IcPackage className="mx-auto text-4xl text-gray-300 mb-3" />
                    <p className="text-gray-500">Aucun produit trouvé</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* « soins masqués » amber banner removed per owner Studio */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 pt-2">
                  {filteredServices.map((service) => {
                    const price = parseFloat(service.price) || 0;
                    const remise = parseFloat(service.remise) || 0;
                    const finalPrice = remise > 0 ? price * (1 - remise / 100) : price;
                    const inCart = selectedServices.find((s) => s.id === service.id);
                    return (
                      <div key={service.id} className="kp-lift group relative flex flex-col bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-200">
                        <div className="relative h-20 bg-gray-100 flex items-center justify-center">
                          <IcSparkles className="text-black group-hover:scale-110 transition-transform" size={28} />
                          {remise > 0 && (
                            <span className="bdg bdg-promo absolute top-2 right-2 shadow-sm">−{remise}%</span>
                          )}
                          {inCart && (
                            <div className="bdg-count-ink top-auto bottom-2 right-2 shadow ring-2 ring-white/60">{inCart.quantity || 1}</div>
                          )}
                        </div>
                        <div className="flex flex-col flex-grow p-3">
                          <h4 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2 min-h-[2.5rem] mb-1">{service.name}</h4>
                          {service.description && (<p className="text-[11px] text-gray-400 mb-2 line-clamp-2">{service.description}</p>)}
                          <div className="mt-auto mb-2">
                            <p className="text-lg font-extrabold text-black leading-none">{finalPrice.toFixed(3)}<span className="text-xs font-semibold text-gray-400 ml-1">DT</span></p>
                            {remise > 0 && (<span className="text-[11px] text-gray-400 line-through">{price.toFixed(3)} DT</span>)}
                          </div>
                          <button onClick={() => addService(service)} className="btn-p w-full py-2 px-3 text-xs transition-all">
                            <IcPlus className="mr-1.5" size={10} />
                            {inCart ? 'Ajouter encore' : 'Ajouter'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {filteredServices.length === 0 && (
                  <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center mt-2">
                    <IcSparkles className="mx-auto text-4xl text-gray-300 mb-3" />
                    <p className="text-gray-500">{hiddenSoins.length > 0 ? 'Aucun service disponible — les soins nécessitent un rendez-vous pour ce client.' : 'Aucun service trouvé'}</p>
                  </div>
                )}
              </>
            )}
          </div>

            </div>

            {/* ---------- Carte transaction ----------
                Hauteur fixe comme la carte produits : la carte ne défile PAS,
                seuls les articles défilent, et leur barre de défilement est
                masquée (owner 2026-09-07). En-tête = la recherche client :
                titre « Vente en cours », mention « Passager » et pastille
                d'icône retirés à la demande de l'owner. */}
            <div className="kp-card kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">

              {/* En-tête : recherche client. Quand un client est choisi, son
                  identité et sa fidélité prennent la place du champ. */}
              <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
                {(isPassagerCustomer() || !selectedCustomer) ? (
                  <div className="relative">
                    <IcSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={13} />
                    <input
                      type="text"
                      value={customerQuery}
                      onChange={(e) => { const v = e.target.value; setCustomerQuery(v); if (v.trim() === '') { setShowCustomers(false); } else { searchCustomers(v); setShowCustomers(true); } }}
                      onFocus={() => { if (customers.length === 0) loadCustomers(false); if (customerQuery.trim()) setShowCustomers(true); }}
                      onBlur={() => setTimeout(() => setShowCustomers(false), 150)}
                      placeholder="Rechercher un client..."
                      className="inp w-full pl-9 pr-3 py-2.5 text-sm"
                    />
                    {showCustomers && customers.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white rounded-lg shadow-lg border border-gray-100 py-1 max-h-52 overflow-y-auto">
                        <button onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowClientPicker(true); setShowCustomers(false); }} className="w-full px-3 py-1.5 text-left text-blue-600 hover:bg-blue-50 text-xs flex items-center gap-1.5 border-b border-gray-100">
                          <IcPlus size={9} /> Plus d'options / ajouter un client
                        </button>
                        {customers.map((c) => (
                          <button key={c.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { selectCustomer(c); setShowCustomers(false); }} className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-gray-800 truncate">{c.first_name || ''} {c.last_name || ''}</span>
                            {c.phone && <span className="text-xs text-gray-400 flex-shrink-0">{c.phone}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button type="button" onClick={() => resetToPassager()} className="w-full text-left" title="Cliquer pour revenir à Passager">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-base font-bold text-gray-900 truncate min-w-0">{`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || selectedCustomer.name || 'Client'}</span>
                      {customerCategory?.name && (
                        <span className={`bdg flex-shrink-0 ${
                          (customerCategory.name || '').toLowerCase().includes('gold') ? 'bdg-gold' : 'bdg-fidele'
                        } text-xs py-[3px]`}>
                          {customerCategory.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-1.5">
                      <span className="bdg bdg-score rounded-full text-xs px-2.5 py-0.5 tabular-nums">{parseInt(selectedCustomer.score || 0)}</span>
                      <span className="bdg bdg-points rounded-full text-sm px-3 py-1 tabular-nums">{Math.round(selectedCustomer.points || 0)}</span>
                      {calculatePointsGain() > 0 && (
                        returnMode
                          ? <span className="bdg bdg-down ml-auto">−{Math.floor(calculatePointsGain())}</span>
                          : <span className="bdg bdg-pos ml-auto">+{Math.floor(calculatePointsGain())}</span>
                      )}
                    </div>
                  </button>
                )}
                {/* Mode retour : sous le nom du client (owner 2026-09-28) —
                    « Annuler » revient à la vente normale. */}
                {/* Qui la caméra a reconnu en arrière-plan (caisse). */}
                {returnMode && (
                  <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 flex items-center gap-2 text-xs text-red-700">
                    <IcUndo size={13} className="flex-shrink-0" />
                    <span className="min-w-0"><span className="font-semibold">Retour client</span> — tout le panier revient en stock et est remboursé.</span>
                    <button type="button" onClick={exitReturnMode} className="ml-auto flex-shrink-0 px-2.5 py-1 rounded-xl bg-gray-100 text-gray-700 font-medium hover:bg-gray-200">Annuler</button>
                  </div>
                )}
              </div>

              {/* Articles — occupent TOUT l'espace restant (flex-1) et sont la
                  seule zone qui défile ; la barre de défilement est masquée,
                  même recette que la bande d'onglets (VenteTabs). Ligne = image
                  + nom + prix + pas de quantité, et le ✕ retourne en haut à
                  droite comme sur l'ancien rail épinglé. */}
              <div className="kp-scroll p-3 space-y-2">
                {(selectedProducts.length === 0 && selectedServices.length === 0) ? (
                  <div className="h-full flex items-center justify-center p-4">
                    <div className="empty-ki w-full px-4 py-8">
                      <IcShoppingCart size={28} className="empty-ki-icon w-[28px] h-[28px] mb-2" />
                      <p className="empty-ki-title text-sm mt-0">Aucun article</p>
                      <p className="empty-ki-sub mb-0">Scannez un code-barres ou cliquez sur un produit.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {selectedProducts.map((p) => (
                      <div key={`ln-p-${p.id}`} className="kp-pin-chip group/line relative rounded-xl border border-gray-200 bg-white px-3 py-2.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start gap-2.5">
                          <span className="h-10 w-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {p.image
                              ? <img src={p.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              : <IcPackage size={16} className="text-gray-300" />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-semibold text-gray-900 leading-snug truncate pr-6" title={p.name}>{p.name}</p>
                            <div className="flex items-center justify-between gap-2 mt-1.5">
                              <span className="text-[13px] font-bold text-gray-900 tabular-nums whitespace-nowrap">
                                {(p.price * p.quantity).toFixed(3)}<span className="text-[10px] font-medium text-gray-400 ml-1">DT</span>
                                {p.promo && <span className="bdg bdg-promo ml-1.5">PROMO</span>}
                              </span>
                              <span className="flex items-center gap-1 flex-shrink-0">
                                <button type="button" onClick={() => updateQuantity(p.id, p.quantity - 1)} aria-label="Diminuer" className="h-6 w-6 rounded-md bg-white border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600 flex items-center justify-center transition-all"><IcMinus size={8} /></button>
                                <span className="w-5 text-center text-xs font-extrabold text-gray-800 tabular-nums">{p.quantity}</span>
                                <button type="button" onClick={() => updateQuantity(p.id, p.quantity + 1)} aria-label="Augmenter" className="h-6 w-6 rounded-md bg-white border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600 flex items-center justify-center transition-all"><IcPlus size={8} /></button>
                              </span>
                            </div>
                          </div>
                        </div>
                        {returnMode && returnHint(p)}
                        <button type="button" onClick={() => removeFromCart(p.id)} title="Retirer du panier"
                          className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full border-[1.5px] border-gray-300 text-gray-500 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors">
                          <IcClose size={13} />
                        </button>
                      </div>
                    ))}
                    {selectedServices.map((s) => {
                      const gratuit = isServiceGratuit(s);
                      const lineTotal = ((parseFloat(s.price) || 0) * (1 - (parseFloat(s.remise) || 0) / 100)) * (s.quantity || 1);
                      return (
                        <div key={`ln-s-${s.id}`} className="kp-pin-chip group/line relative rounded-xl border border-green-200 bg-white px-3 py-2.5 hover:border-green-300 hover:bg-gray-100 transition-colors">
                          <div className="flex items-start gap-2.5">
                            <span className="h-10 w-10 rounded-lg bg-white border border-green-100 text-green-600 flex items-center justify-center flex-shrink-0"><IcSparkles size={16} /></span>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-semibold text-green-800 leading-snug truncate pr-6" title={s.name}>{s.name}</p>
                              <div className="flex items-center justify-between gap-2 mt-1.5">
                                <span className="text-[13px] font-bold text-green-700 tabular-nums whitespace-nowrap">
                                  {gratuit ? 'Gratuit' : (<>{lineTotal.toFixed(3)}<span className="text-[10px] font-medium text-gray-400 ml-1">DT</span></>)}
                                </span>
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  <button type="button" onClick={() => updateServiceQuantity(s.id, (s.quantity || 1) - 1)} aria-label="Diminuer" className="h-6 w-6 rounded-md bg-white border border-green-200 text-gray-500 hover:text-green-700 flex items-center justify-center transition-all"><IcMinus size={8} /></button>
                                  <span className="w-5 text-center text-xs font-extrabold text-gray-800 tabular-nums">{s.quantity || 1}</span>
                                  <button type="button" onClick={() => updateServiceQuantity(s.id, (s.quantity || 1) + 1)} aria-label="Augmenter" className="h-6 w-6 rounded-md bg-white border border-green-200 text-gray-500 hover:text-green-700 flex items-center justify-center transition-all"><IcPlus size={8} /></button>
                                </span>
                              </div>
                            </div>
                          </div>
                          <button type="button" onClick={() => removeService(s.id)} title="Retirer du panier"
                            className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full border-[1.5px] border-gray-300 text-gray-500 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors">
                            <IcClose size={13} />
                          </button>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Argent — Remise (1/3) et Montant payé (2/3) sur la MÊME ligne ;
                  les raccourcis +5/+10/+20/+50 et « Effacer » ont été retirés
                  (owner 2026-09-07). Mêmes états/handlers que le panier. */}
              <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0 space-y-3">
                {!returnMode && pointsAvailable > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-gray-900 flex items-center gap-1"><IcGift size={11} className="text-green-600" /> Payer avec points</label>
                      <span className="text-[11px] text-gray-500">Dispo : <span className="font-semibold text-green-700">{Math.floor(pointsAvailable)}</span></span>
                    </div>
                    <div className="relative">
                      <input type="number" step="1" min="0" value={pointsToUseRaw !== null ? pointsToUseRaw : (pointsPay || '')} onChange={(e) => { setPointsToUseRaw(e.target.value); let v = parseFloat(e.target.value); if (e.target.value === '' || isNaN(v) || v < 0) v = 0; setPointsToUse(v); setAmountPaidTouched(false); }} onFocus={(e) => e.target.select()} onBlur={() => setPointsToUseRaw(null)} placeholder="0" className="inp w-full px-3 py-2.5 pr-20 text-sm font-semibold text-gray-900 tabular-nums" />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <button type="button" onClick={() => { setPointsToUse(Math.min(pointsAvailable, totalAmount)); setAmountPaidTouched(false); }} className="px-2 py-0.5 rounded-md bg-green-600 text-white text-[11px] font-semibold hover:bg-green-700">Max</button>
                        <span className="text-xs text-gray-400">pts</span>
                      </div>
                    </div>
                    {pointsPay > 0 && (<span className="text-[10px] text-green-600 font-medium">−{round3(pointsPay)} DT payés en points · reste {dueAfterPoints.toFixed(3)} DT</span>)}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2 items-start">
                  <div className="col-span-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-900 mb-1.5">Remise</label>
                    <div className="relative">
                      <input type="number" step="0.5" min="0" max="100" value={discountRaw !== null ? discountRaw : discount} onChange={(e) => { setDiscountRaw(e.target.value); let v = parseFloat(e.target.value); if (e.target.value === '' || isNaN(v) || v < 0) v = 0; if (v > 100) v = 100; setDiscount(v); }} onFocus={(e) => e.target.select()} onBlur={() => setDiscountRaw(null)} placeholder="0" className="inp w-full px-2.5 py-2.5 pr-6 text-sm font-semibold text-gray-900" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                    </div>
                  </div>
                  {returnMode ? (
                  <div className="col-span-2 min-w-0">
                    <label className="block text-xs font-medium text-gray-900 mb-1.5">Remboursement</label>
                    {refundChooser}
                  </div>
                  ) : (
                  <div className="col-span-2 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <label className="block text-xs font-medium text-gray-900 truncate">Montant payé{pointsPay > 0 && <span className="text-[10px] text-gray-400 font-normal"> (espèces)</span>}</label>
                      {dueAfterPoints > 0 && (<button type="button" onClick={() => setAmountPaidExact(dueAfterPoints)} className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 flex-shrink-0">Montant exact</button>)}
                    </div>
                    <div className="relative">
                      <input type="number" step="0.001" min="0" value={amountPaidRaw !== null ? amountPaidRaw : amountPaid} onChange={(e) => handleAmountPaidChange(e.target.value)} onFocus={(e) => e.target.select()} onBlur={() => setAmountPaidRaw(null)} placeholder="0.000" className="inp w-full px-3 py-2.5 pr-10 text-base font-bold text-gray-900 tabular-nums" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">DT</span>
                    </div>
                  </div>
                  )}
                </div>
                {totalRemise > 0 && <span className="block text-[10px] text-gray-400">Remise = {totalRemise.toFixed(3)} DT</span>}
              </div>

              {/* Pied — total (point focal), puis les deux actions sur la MÊME
                  ligne : « Détails du panier » sur 1/3, « Encaisser » sur 2/3. */}
              <div className="p-3 border-t border-gray-100 flex-shrink-0">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-500">{returnMode ? 'Total à rembourser' : 'Total à payer'}</span>
                  <span className="text-2xl font-bold text-gray-900 tabular-nums">{totalAmount.toFixed(3)} DT</span>
                </div>
                {(pointsPay > 0 || avancePay > 0 || avanceSurplus > 0) && (
                  <div className="text-[11px] mb-2 space-y-0.5">
                    {pointsPay > 0 && (<div className="flex justify-between text-green-700"><span>Payé en points</span><span className="tabular-nums">−{round3(pointsPay)} DT</span></div>)}
                    {avancePay > 0 && (<div className="flex justify-between text-green-700"><span>Avance réservation</span><span className="tabular-nums">−{round3(avancePay)} DT</span></div>)}
                    {avanceSurplus > 0 && (<div className="flex justify-between text-orange-600"><span>À rendre (avance &gt; total)</span><span className="tabular-nums">{round3(avanceSurplus)} DT</span></div>)}
                  </div>
                )}
                {returnMode && selectedProducts.length > 0 && (
                  <div className="rounded-lg px-3 py-2 mb-2.5 border bg-red-50 border-red-200 space-y-0.5">
                    {refundCreditPart > 0 && (<div className="flex justify-between text-xs text-red-700"><span>Déduit du crédit</span><span className="tabular-nums">{refundCreditPart.toFixed(3)} DT</span></div>)}
                    <div className="flex justify-between items-center"><span className="text-sm font-semibold text-red-700">À rendre au client</span><span className="text-lg font-bold text-red-700 tabular-nums">{refundCash.toFixed(3)} DT</span></div>
                  </div>
                )}
                {!returnMode && (selectedProducts.length > 0 || selectedServices.length > 0) && (
                  amountPaid > dueAfterPoints ? (
                    <div className="flex justify-between items-center bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2.5">
                      <span className="text-sm font-semibold text-green-700">Monnaie à rendre</span>
                      <span className="text-lg font-bold text-green-700 tabular-nums">{(amountPaid - dueAfterPoints).toFixed(3)} DT</span>
                    </div>
                  ) : (
                    <div className={`flex justify-between items-center rounded-lg px-3 py-2 mb-2.5 border ${(dueAfterPoints - amountPaid) > 0.0005 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                      <span className={`text-sm font-semibold ${(dueAfterPoints - amountPaid) > 0.0005 ? 'text-red-700' : 'text-green-700'}`}>{(dueAfterPoints - amountPaid) > 0.0005 ? 'Reste à payer' : 'Soldé'}</span>
                      <span className={`text-lg font-bold tabular-nums ${(dueAfterPoints - amountPaid) > 0.0005 ? 'text-red-700' : 'text-green-700'}`}>{Math.max(0, dueAfterPoints - amountPaid).toFixed(3)} DT</span>
                    </div>
                  )
                )}
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => setShowPanier(true)} className="btn-g col-span-1 py-2.5 rounded-xl text-xs transition-colors">
                    Détails
                  </button>
                  <button
                    type="button"
                    onClick={returnMode ? handleSubmitReturn : handleSubmitSale}
                    disabled={submitting || (selectedProducts.length === 0 && selectedServices.length === 0) || !selectedCustomer}
                    className={`${returnMode ? 'btn-db' : 'btn-p'} col-span-2 py-2.5 rounded-xl text-sm gap-2 transition-all`}
                  >
                    {submitting
                      ? (<><BtnSk /> Enregistrement...</>)
                      : returnMode
                        ? (<><IcUndo size={14} /> Valider le retour</>)
                        : (<><IcBanknote size={14} /> Encaisser la vente</>)}
                  </button>
                </div>
              </div>
            </div>
          </div>

      </div>

      {/* ===== Panier popup (review + confirm) ===== */}
      {showPanier && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowPanier(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden flex flex-col max-h-[90vh]">
            {/* Brand Kit §05 panier : entête aplat encre 13/16px (modal-head),
                titre 600 16px + icône panier 16px (gap 9), ✕ 17px #F3F4F6 ; 640px */}
            <div className="modal-head px-5 py-3 flex-shrink-0">
              <span className="modal-title flex items-center gap-2 flex-wrap">
                <IcShoppingCart /> Panier
                {selectedCustomer && (
                  <span className="text-gray-100 font-normal truncate max-w-[12rem]">· {isPassagerCustomer() ? 'Passager' : (`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || selectedCustomer.name || 'Client')}</span>
                )}
                {/* Livre §05 entête panier : « FIDÈLE GOLD » aplat #F59E0B 10.5px
                    (sans médaille) + un seul chip « N produits · M services » #374151. */}
                {selectedCustomer && !isPassagerCustomer() && customerCategory?.name && (
                  (customerCategory.name || '').toLowerCase().includes('gold')
                    ? <span className="bdg bdg-gold-flat rounded-full text-xs px-2.5 py-[3px]">{customerCategory.name}</span>
                    : <span className="bdg bdg-fidele rounded-full text-xs px-2.5 py-[3px]">{customerCategory.name}</span>
                )}
                {selectedCustomer && !isPassagerCustomer() && (
                  <span className="bdg bdg-points rounded-full text-xs px-2.5 py-[3px] tabular-nums" title={returnMode ? 'Total points du client après ce retour' : 'Total points du client après cette vente'}>
                    Points {Math.floor(Math.max(0, (parseFloat(selectedCustomer.points) || 0) + (returnMode ? -1 : 1) * calculatePointsGain()))}
                  </span>
                )}
                <span className="bdg bdg-cart rounded-full text-xs px-2.5 py-[3px] tabular-nums">
                  {selectedProducts.length} produit{selectedProducts.length > 1 ? 's' : ''} · {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''}
                </span>
              </span>
              <button onClick={() => setShowPanier(false)} className="text-gray-100 hover:text-white p-1.5 rounded-full shrink-0" aria-label="Fermer"><IcClose /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {selectedProducts.length === 0 && selectedServices.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <IcShoppingCart size={32} className="mx-auto mb-3 text-gray-300" />
                  <p className="font-semibold text-gray-600">Votre panier est vide</p>
                </div>
              ) : (
                <>
                  {/* Lignes du panier (Brand Kit §05) : lignes plates 13px,
                      11px de haut/bas, séparées par un filet gray-100 — nom
                      600, chips −N% / GRATUIT, montant 600 tabulaire */}
                  <div className="divide-y divide-gray-100 space-y-2">
                    {selectedProducts.map((product) => {
                      const originalProduct = products.find((p) => p.id === product.id);
                      // Per-line discount is a % (pre-filled from the facture/client remise).
                      const linePctNum = Math.min(Math.max(parseFloat(product.remisePct) || 0, 0), 100);
                      const linePct = linePctNum > 0 ? linePctNum : '';
                      const lineTotal = Math.max(0, product.price * product.quantity * (1 - linePctNum / 100));
                      return (
                        <div key={product.id} className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex items-start gap-2">
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 text-sm truncate">{product.name}</p>
                                <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                                  <span>{product.price.toFixed(3)} DT</span>
                                  <span className={`font-medium ${originalProduct?.stock < 10 ? 'text-orange-600' : 'text-green-600'}`}>· Stock {originalProduct ? sellableStock(originalProduct) : 0}</span>
                                  {clientBlockedBonus[product.id] && (
                                    <span className="font-semibold text-teal-600 flex items-center gap-1"><IcLock size={8} /> bloqué pour ce client</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button onClick={() => removeFromCart(product.id)} title="Retirer" className="p-1 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 flex-shrink-0 transition-colors"><IcTrash /></button>
                          </div>
                          <div className="mt-2.5 flex items-center justify-between gap-2 flex-wrap">
                            {/* Boutons-2 §7 stepper : cases 40px blanches texte encre, groupe bordé #D1D5DB rayon 6 */}
                            <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden">
                              <button onClick={() => updateQuantity(product.id, product.quantity - 1)} className="w-9 h-9 flex items-center justify-center bg-white hover:bg-gray-100 text-gray-900 transition"><IcMinus className="text-xs" /></button>
                              <span className="w-8 text-center font-semibold tabular-nums">{product.quantity}</span>
                              <button onClick={() => updateQuantity(product.id, product.quantity + 1)} disabled={!returnMode && originalProduct && product.quantity >= sellableStock(originalProduct)} className="w-9 h-9 flex items-center justify-center bg-white hover:bg-gray-100 text-gray-900 transition"><IcPlus className="text-xs" /></button>
                            </div>
                            {product.promo ? (
                              <span className="bdg bdg-promo whitespace-nowrap">PROMO · sans remise</span>
                            ) : (
                              <div className="relative w-20">
                                <input type="number" step="1" min="0" max="100" value={linePct} onChange={(e) => updateProductRemisePercent(product.id, e.target.value)} onFocus={(e) => e.target.select()} placeholder={factureRemisePercent > 0 ? `${factureRemisePercent}` : 'Remise'} className="w-full pl-2 pr-6 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900" />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">%</span>
                              </div>
                            )}
                            <span className="font-semibold text-gray-900 text-sm tabular-nums ml-auto">{lineTotal.toFixed(3)} DT</span>
                          </div>
                          {returnMode && returnHint(product)}
                        </div>
                      );
                    })}
                    {selectedServices.map((service) => {
                      const price = parseFloat(service.price) || 0;
                      const remise = parseFloat(service.remise) || 0;
                      const finalPrice = remise > 0 ? price * (1 - remise / 100) : price;
                      const gratuit = isServiceGratuit(service);
                      const ptsCost = servicePointsCost(service); // null = pas de prix en points (Avantages)
                      const paidWithPoints = isServicePaidWithPoints(service);
                      const ptsLineCost = round3((ptsCost || 0) * (service.quantity || 1));
                      const totalService = (gratuit || paidWithPoints) ? 0 : finalPrice * (service.quantity || 1);
                      return (
                        <div key={`service-${service.id}`} className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex items-start gap-2">
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 text-sm truncate">{service.name}</p>
                                <div className="flex items-center gap-2 text-xs flex-wrap">
                                  {gratuit ? (
                                    <>
                                      <span className="bdg bdg-free bg-white">GRATUIT</span>
                                      <span className="text-gray-400 line-through">{finalPrice.toFixed(3)} DT</span>
                                    </>
                                  ) : paidWithPoints ? (
                                    <>
                                      <span className="bdg bdg-pts">{ptsLineCost} PTS</span>
                                      <span className="text-gray-400 line-through">{finalPrice.toFixed(3)} DT</span>
                                    </>
                                  ) : remise > 0 ? (
                                    <>
                                      <span className="font-medium text-green-700">{finalPrice.toFixed(3)} DT</span>
                                      <span className="text-gray-400 line-through">{price.toFixed(3)}</span>
                                      <span className="bdg bdg-remise">−{remise}%</span>
                                    </>
                                  ) : (
                                    <span className="text-gray-500">{price.toFixed(3)} DT</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button onClick={() => removeService(service.id)} title="Retirer" className="p-1 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 flex-shrink-0 transition-colors"><IcTrash /></button>
                          </div>
                          <div className="mt-2.5 flex items-center justify-between gap-2">
                            <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden">
                              <button onClick={() => updateServiceQuantity(service.id, (service.quantity || 1) - 1)} className="w-9 h-9 flex items-center justify-center bg-white hover:bg-gray-100 text-gray-900 transition"><IcMinus className="text-xs" /></button>
                              <span className="w-8 text-center font-semibold tabular-nums">{service.quantity || 1}</span>
                              <button onClick={() => updateServiceQuantity(service.id, (service.quantity || 1) + 1)} className="w-9 h-9 flex items-center justify-center bg-white hover:bg-gray-100 text-gray-900 transition"><IcPlus className="text-xs" /></button>
                            </div>
                            {serviceCanBeGratuit(service) && (
                              <button
                                type="button"
                                onClick={() => toggleServiceGratuit(service.id)}
                                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors ${gratuit ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                                title={gratuit ? 'Annuler la gratuité (facturer)' : 'Offrir ce service gratuitement'}
                              >
                                <IcGift size={11} /> {gratuit ? 'Payant' : 'Gratuit'}
                              </button>
                            )}
                            {ptsCost !== null && (() => {
                              const canToggleOn = pointsBalance - soinsPointsSpend + 1e-6 >= ptsLineCost;
                              return (
                                <button
                                  type="button"
                                  onClick={() => toggleServicePayWithPoints(service.id)}
                                  disabled={!paidWithPoints && !canToggleOn}
                                  className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors ${paidWithPoints ? 'bg-green-600 text-white hover:bg-green-700' : canToggleOn ? 'bg-white border border-green-300 text-green-700 hover:bg-green-50' : 'bg-white border border-gray-200 text-gray-300 cursor-not-allowed'}`}
                                  title={paidWithPoints ? 'Annuler le paiement en points (facturer en DT)' : canToggleOn ? `Payer ce soin avec ${ptsLineCost} points (Avantages catégorie)` : `Points insuffisants (coût : ${ptsLineCost} pts)`}
                                >
                                  <IcGift size={11} /> {paidWithPoints ? 'En points' : `Points (${ptsLineCost})`}
                                </button>
                              );
                            })()}
                            <span className="font-semibold text-gray-900 text-sm tabular-nums ml-auto">{paidWithPoints ? `${ptsLineCost} pts` : `${totalService.toFixed(3)} DT`}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Remise */}
                  <div>
                    <div className="grid grid-cols-2 gap-2 mb-1">
                      <label className="text-xs font-medium text-gray-700">Remise</label>
                      <label className="text-xs font-medium text-gray-700">{returnMode ? 'Montant à rembourser' : 'Montant à recevoir'}</label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <input type="number" step="0.5" min="0" max="100" value={discountRaw !== null ? discountRaw : discount} onChange={(e) => { setDiscountRaw(e.target.value); let v = parseFloat(e.target.value); if (e.target.value === '' || isNaN(v) || v < 0) v = 0; if (v > 100) v = 100; setDiscount(v); }} onFocus={(e) => e.target.select()} onBlur={() => setDiscountRaw(null)} placeholder="0" className="w-full pl-2 pr-9 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">%</span>
                      </div>
                      <div className="relative">
                        <input type="number" step="0.001" min="0" value={recevoirRaw !== null ? recevoirRaw : Number(totalAmount.toFixed(3))} onChange={(e) => { setRecevoirRaw(e.target.value); setRemiseFromRecevoir(e.target.value); }} onFocus={(e) => e.target.select()} onBlur={() => setRecevoirRaw(null)} placeholder="0.000" className="w-full pl-2 pr-9 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">DT</span>
                      </div>
                    </div>
                    <div className="mt-0.5">
                      {totalRemise > 0 && <span className="text-[10px] text-gray-400">Remise = {totalRemise.toFixed(3)} DT</span>}
                    </div>
                  </div>

                  {/* Payer avec points (fidèle / fidèle gold) — 1 pt = 1 DT */}
                  {!returnMode && pointsAvailable > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-700 flex items-center gap-1"><IcGift size={11} className="text-green-600" /> Payer avec points <span className="text-[10px] text-gray-400">(1 pt = 1 DT)</span></label>
                        <span className="text-[11px] text-gray-500">Dispo: <span className="font-semibold text-green-700">{Math.floor(pointsAvailable)}</span></span>
                      </div>
                      <div className="relative">
                        <input type="number" step="1" min="0" value={pointsToUseRaw !== null ? pointsToUseRaw : (pointsPay || '')} onChange={(e) => { setPointsToUseRaw(e.target.value); let v = parseFloat(e.target.value); if (e.target.value === '' || isNaN(v) || v < 0) v = 0; setPointsToUse(v); setAmountPaidTouched(false); }} onFocus={(e) => e.target.select()} onBlur={() => setPointsToUseRaw(null)} placeholder="0" className="w-full pl-3 pr-24 py-2 text-sm font-semibold border border-green-300 rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900" />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <button type="button" onClick={() => { setPointsToUse(Math.min(pointsAvailable, totalAmount)); setAmountPaidTouched(false); }} className="px-2 py-0.5 rounded-md bg-green-600 text-white text-[11px] font-semibold hover:bg-green-700">Max</button>
                          <span className="text-xs text-gray-400">pts</span>
                        </div>
                      </div>
                      {pointsPay > 0 && (<span className="text-[10px] text-green-600 font-medium">−{round3(pointsPay)} DT payés en points · reste {dueAfterPoints.toFixed(3)} DT</span>)}
                    </div>
                  )}

                  {/* Remboursement (mode retour) */}
                  {returnMode && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Remboursement</label>
                      {refundChooser}
                    </div>
                  )}

                  {/* Montant payé */}
                  {!returnMode && (<div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-medium text-gray-700">Montant Payé{pointsPay > 0 && <span className="text-[10px] text-gray-400 font-normal"> (en espèces)</span>}</label>
                    </div>
                    <div className="relative">
                      <input type="number" step="0.001" min="0" value={amountPaidRaw !== null ? amountPaidRaw : amountPaid} onChange={(e) => handleAmountPaidChange(e.target.value)} onFocus={(e) => e.target.select()} onBlur={() => setAmountPaidRaw(null)} placeholder="0.000" className="w-full pl-2 pr-9 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">DT</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {[5, 10, 20, 50].map((d) => (
                        <button key={d} type="button" onClick={() => { setAmountPaidTouched(true); setAmountPaid((p) => Math.round(((Number(p) || 0) + d) * 1000) / 1000); }} className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-blue-50 hover:border-blue-300 transition-colors">+{d}</button>
                      ))}
                      <button type="button" onClick={() => setAmountPaidExact(0)} className="ml-auto px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors">Effacer</button>
                    </div>
                  </div>)}

                  {/* Résumé */}
                  <div className="bg-gray-50 border border-gray-100 p-3 rounded-2xl">
                    <h3 className="font-semibold text-gray-800 mb-2 text-xs uppercase tracking-wide">{returnMode ? 'Résumé du retour' : 'Résumé de la Vente'}</h3>
                    <div className="space-y-1 text-xs">
                      {selectedProducts.length > 0 && (
                        <div className="flex justify-between text-gray-900"><span className="flex items-center"><IcPackage className="mr-1" size={10} /> Produits ({selectedProducts.length}):</span><span>{productsSubtotal.toFixed(3)} DT</span></div>
                      )}
                      {selectedServices.length > 0 && (
                        <div className="flex justify-between text-green-700"><span className="flex items-center"><IcSparkles className="mr-1" size={10} /> Services ({selectedServices.length}):</span><span>{servicesSubtotal.toFixed(3)} DT</span></div>
                      )}
                      <div className="flex justify-between border-t pt-1"><span>Sous-total:</span><span className="font-medium">{subtotal.toFixed(3)} DT</span></div>
                      {totalRemise > 0 && (<div className="flex justify-between"><span>Remise:</span><span className="text-red-600">-{totalRemise.toFixed(3)} DT</span></div>)}
                      <div className="flex justify-between font-bold text-lg border-t pt-2"><span>{returnMode ? 'Total à rembourser:' : 'Total à Payer:'}</span><span>{totalAmount.toFixed(3)} DT</span></div>
                      {soinsPointsSpend > 0 && (<div className="flex justify-between text-green-700"><span className="flex items-center"><IcGift className="mr-1" size={10} /> Soin(s) payé(s) en points:</span><span>{round3(soinsPointsSpend)} pts</span></div>)}
                      {pointsPay > 0 && (<div className="flex justify-between text-green-700"><span className="flex items-center"><IcGift className="mr-1" size={10} /> Payé en points:</span><span>-{round3(pointsPay)} DT</span></div>)}
                      {avancePay > 0 && (<div className="flex justify-between text-green-700"><span className="flex items-center"><IcPin className="mr-1" size={10} /> Avance réservation:</span><span>-{round3(avancePay)} DT</span></div>)}
                      {avanceSurplus > 0 && (<div className="flex justify-between text-orange-600"><span className="flex items-center"><IcAlertTriangle className="mr-1" size={10} /> À rendre au client (avance &gt; total):</span><span>{round3(avanceSurplus)} DT</span></div>)}
                      {returnMode ? (<>
                        {refundCreditPart > 0 && (<div className="flex justify-between text-gray-900 border-t pt-2"><span>Déduit du crédit :</span><span>{refundCreditPart.toFixed(3)} DT</span></div>)}
                        <div className="flex justify-between items-center bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-1"><span className="text-sm font-semibold text-red-700">À rendre au client</span><span className="text-lg font-bold text-red-700">{refundCash.toFixed(3)} DT</span></div>
                      </>) : (<>
                      <div className="flex justify-between text-gray-900 border-t pt-2"><span>Montant Payé{pointsPay > 0 ? ' (espèces)' : ''}:</span><span>{amountPaid.toFixed(3)} DT</span></div>
                      {amountPaid > dueAfterPoints ? (
                        <div className="flex justify-between items-center bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-1"><span className="text-sm font-semibold text-green-700">Monnaie à rendre</span><span className="text-lg font-bold text-green-700">{(amountPaid - dueAfterPoints).toFixed(3)} DT</span></div>
                      ) : (
                        <div className={`flex justify-between items-center rounded-lg px-3 py-2 mt-1 border ${(dueAfterPoints - amountPaid) > 0.0005 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                          <span className={`text-sm font-semibold ${(dueAfterPoints - amountPaid) > 0.0005 ? 'text-red-700' : 'text-green-700'}`}>{(dueAfterPoints - amountPaid) > 0.0005 ? 'Reste à payer' : 'Soldé'}</span>
                          <span className={`text-lg font-bold ${(dueAfterPoints - amountPaid) > 0.0005 ? 'text-red-700' : 'text-green-700'}`}>{Math.max(0, dueAfterPoints - amountPaid).toFixed(3)} DT</span>
                        </div>
                      )}
                      </>)}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Pied panier (Brand Kit §05) : filet gray-100, sortie ghost +
                Encaisser = primaire vert (§03). Le rappel du total (18px 700
                tabulaire à gauche) est RETIRÉ (owner 2026-09-08, Studio
                /seller-sales) : retrait par le JSX, pas par display:none — le
                montant reste lisible sur la ligne « Total à Payer » juste
                au-dessus. */}
            <div className="flex-shrink-0 border-t border-gray-100 p-4 flex items-center justify-between gap-3">
              <span className="flex-1 flex items-center gap-3">
                <button onClick={() => setShowPanier(false)} className="btn-g px-5 py-2.5 rounded-xl text-sm">Continuer</button>
                <button onClick={returnMode ? handleSubmitReturn : handleSubmitSale} disabled={submitting || (selectedProducts.length === 0 && selectedServices.length === 0) || !selectedCustomer} className={`${returnMode ? 'btn-db' : 'btn-p'} flex-1 px-4 py-2.5 rounded-xl transition-all`}>
                  {submitting ? (<><BtnSk /> Enregistrement...</>) : returnMode ? (<><IcUndo /> Valider le retour</>) : (<><IcBanknote /> Encaisser la vente</>)}
                </button>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ===== Client picker popup ===== */}
      {showClientPicker && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowClientPicker(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-12 p-5">
            <div className="modal-head -mx-5 -mt-5 mb-4 rounded-t-2xl">
              <h3 className="modal-title text-lg flex items-center gap-2"><IcUser /> Choisir le client</h3>
              <button onClick={() => setShowClientPicker(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>
            <div className="relative">
              <IcSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={selectedCustomer && !isPassagerCustomer() ? `${selectedCustomer.first_name} ${selectedCustomer.last_name || ''}`.trim() : customerQuery}
                onChange={(e) => { const v = e.target.value; setCustomerQuery(v); if (selectedCustomer) { setSelectedCustomer(null); setCustomerCategory(null); setDiscount(0); } if (v.trim() === '') { resetToPassager(); } else { searchCustomers(v); } }}
                placeholder="Rechercher un client..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
              />
            </div>
            <button onClick={() => { resetToPassager(); setCustomerQuery(''); setShowClientPicker(false); }} className="mt-3 w-full text-left px-3 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium">
              <IcUser className="text-emerald-600" size={12} /> Passager (client de passage)
            </button>
            <button onClick={() => { setShowClientPicker(false); setShowAddCustomerModal(true); }} className="btn-add-block mt-2 px-3 py-2.5 gap-2 text-sm">
              <IcPlus size={12} /> Ajouter un nouveau client
            </button>
            <div className="mt-3 max-h-60 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
              {customers.map((customer) => (
                <button key={customer.id} onClick={() => { selectCustomer(customer); setShowClientPicker(false); }} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">{customer.first_name || ''} {customer.last_name || ''}</span>
                  {customer.phone && <span className="text-xs text-gray-400">{customer.phone}</span>}
                </button>
              ))}
              {customers.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Tapez pour rechercher…</p>}
            </div>
          </div>
        </div>
      )}

      {/* ===== Passer une commande popup (reserve out-of-stock products) ===== */}
      {showOrderModal && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) closeOrderModal(); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-12 overflow-hidden flex flex-col max-h-[85vh]">
            {/* header — Composants-2 §10 : aplat encre */}
            <div className="modal-head px-5 py-4">
              <div className="flex items-center gap-3">
                {reserveStep === 'form' && (
                  <button onClick={() => setReserveStep('select')} className="h-9 w-9 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white flex items-center justify-center" title="Retour"><IcChevronLeft /></button>
                )}
                <span className="h-10 w-10 rounded-xl bg-gray-900 border border-gray-900 text-gray-500 flex items-center justify-center"><IcClipboardList /></span>
                <div>
                  <h3 className="modal-title text-lg leading-tight">{reserveStep === 'form' ? 'Finaliser la réservation' : 'Passer une commande'}</h3>
                  {/* header subtitle removed per owner Studio */}
                </div>
              </div>
              <button onClick={closeOrderModal} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>

            {/* body */}
            {reserveStep === 'select' ? (
              <>
                {/* search bar with « Nouveau produit » dropdown (like Ajouter facture) */}
                <div className="px-5 pt-4">
                  <div className="relative">
                    <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                    <input
                      type="text"
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      onFocus={() => setOrderSearchFocus(true)}
                      onBlur={() => setTimeout(() => setOrderSearchFocus(false), 150)}
                      placeholder="Rechercher un produit (nom ou code)..."
                      className="w-full pl-11 pr-4 py-2.5 border border-white rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 bg-white"
                    />
                    {orderSearchFocus && (
                      <div className="absolute z-20 mt-1 w-full bg-white shadow-xl rounded-lg border border-gray-100 overflow-hidden">
                        <div
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setNewProduct({ name: orderSearch.trim(), cost: '' }); setShowNewProductForm(true); setOrderSearchFocus(false); }}
                          className="btn-add-block px-4 py-2.5 text-sm cursor-pointer"
                        >
                          <IcPlus className="mr-2" />
                          Ajouter un produit{orderSearch.trim() ? ` « ${orderSearch.trim()} »` : ' (hors catalogue)'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* product cases (multi-select grid) */}
                <div className="p-5 pt-3 overflow-y-auto">
                  {orderSearchProducts.length === 0 ? (
                    <div className="text-center py-12">
                      {outOfStockProducts.length === 0 ? (
                        <>
                          <IcCheckCircle className="mx-auto text-4xl text-emerald-400 mb-3" />
                          <p className="text-gray-500">Aucun produit en rupture de stock.</p>
                          <p className="text-gray-400 text-sm mt-1">Cherchez un produit pour le réserver, même en stock.</p>
                        </>
                      ) : (
                        <>
                          <IcPackage className="mx-auto text-4xl text-gray-300 mb-3" />
                          <p className="text-gray-500">Aucun produit trouvé.</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {orderSearchProducts.map((product) => {
                        const selected = isReserveSelected(product);
                        // Même pastille de stock que « Liste des produits ».
                        const stockVal = Number(product.stock || 0);
                        const isOut = stockVal <= 0;
                        const isLow = !isOut && stockVal <= Number(product.stock_min || 0);
                        const stockPill = isOut ? 'bdg-rupture' : isLow ? 'bdg-stock-low' : 'bdg-stock-ok';
                        const stockLabel = isOut ? 'Rupture' : isLow ? `${stockVal} Stock bas` : `${stockVal} En Stock`;
                        return (
                          <button
                            type="button"
                            key={product.id}
                            onClick={() => toggleReserveProduct(product)}
                            className={`group relative text-left flex flex-col bg-white rounded-2xl border overflow-hidden transition-all ${selected ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'}`}
                          >
                            <div className="relative h-24 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                              {product.image ? (
                                <img src={product.image} alt={product.name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center text-gray-300"><IcPackage size={28} /></div>
                              )}
                              <span className={`bdg ${stockPill} absolute top-1.5 left-1.5`}>{stockLabel}</span>
                              {selected && (
                                <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center shadow ring-2 ring-white"><IcCheck size={9} /></span>
                              )}
                            </div>
                            <div className="p-2.5">
                              <p className="text-xs font-semibold text-gray-900 leading-tight line-clamp-2 min-h-[2rem]">{product.name}</p>
                              {(product.code || product.barcode) && (
                                <p className="text-[10px] text-gray-400 truncate mt-0.5">{product.code ? `Code: ${product.code}` : `Barcode: ${product.barcode}`}</p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* footer: Fermer (left) — Réserver (right) */}
                <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center">
                  <button onClick={closeOrderModal} className="btn-g px-5 py-2.5 text-sm">Fermer</button>
                  <button
                    onClick={goToReserveForm}
                    disabled={reserveSelected.length === 0}
                    className="btn-p px-5 py-2.5 rounded-xl text-sm"
                  >
                    <IcClipboardList size={13} /> Réserver{reserveSelected.length > 0 ? ` (${reserveSelected.length})` : ''}
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleCreateReservation} className="flex flex-col overflow-hidden">
                <div className="p-5 overflow-y-auto flex flex-col gap-4">
                  {/* customer name + phone (owner: shown after the panier, order-2) */}
                  <div className="order-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="relative">
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Nom du client <span className="text-red-600">*</span></label>
                      <input
                        type="text"
                        value={reserveForm.customer_name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setReserveForm((f) => ({ ...f, customer_name: v }));
                          searchOrderClients(v);
                        }}
                        onFocus={() => { if (reserveForm.customer_name.trim() && orderClientResults.length) setShowOrderClients(true); }}
                        onBlur={() => setTimeout(() => setShowOrderClients(false), 150)}
                        placeholder="Rechercher ou saisir un nom"
                        autoComplete="off"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                      />
                      {showOrderClients && orderClientResults.length > 0 && (
                        <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                          {orderClientResults.map((c) => (
                            <li key={c.id}>
                              <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); pickOrderClient(c); }}
                                className="w-full text-left px-3 py-2 hover:bg-rose-50 flex items-center justify-between gap-2"
                              >
                                <span className="min-w-0">
                                  <span className="block text-sm font-medium text-gray-800 truncate">{`${c.first_name || ''} ${c.last_name || ''}`.trim()}</span>
                                  <span className="block text-xs text-gray-400 truncate">{c.phone || 'sans téléphone'}</span>
                                </span>
                                {c.category_name && (
                                  <span className={`bdg shrink-0 ${(c.category_name || '').toLowerCase().includes('gold') ? 'bdg-gold' : 'bdg-fidele'}`}>{c.category_name}</span>
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-1 text-[11px] text-gray-400">Choisissez un client fidèle, ou saisissez un nom libre (client passager).</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Téléphone</label>
                      <input
                        type="tel"
                        value={reserveForm.customer_phone}
                        onChange={(e) => setReserveForm({ ...reserveForm, customer_phone: e.target.value })}
                        placeholder="Ex : 29000000"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                      />
                    </div>
                  </div>

                  {/* selected products (owner: panier shown first, order-1) */}
                  <div className="order-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Produits réservés ({reserveLines.length})</label>
                    <div className="space-y-2">
                      {reserveLines.map((line) => {
                        const qty = Math.max(1, Number(line.quantity) || 1);
                        const lineTotal = (Number(line.price) || 0) * qty;
                        return (
                          <div key={line.id} className="rounded-xl border border-gray-200 bg-white p-3 hover:border-rose-200 transition-colors">
                            <div className="flex items-start gap-3">
                              <div className="h-14 w-14 shrink-0 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-100 overflow-hidden flex items-center justify-center text-gray-300">
                                {line.image ? (
                                  <img src={line.image} alt={line.name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                ) : (
                                  <IcPackage size={20} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{line.name}</p>
                                    <p className="text-xs text-gray-400 tabular-nums">
                                      {Number(line.price) > 0 ? `${Number(line.price).toFixed(3)} DT / unité` : 'Prix non défini'}
                                      {line.isNew && <span className="ml-1 text-[10px] font-semibold text-red-500">· estimé (nouveau)</span>}
                                    </p>
                                  </div>
                                  <button type="button" onClick={() => removeReserveLine(line.id)} className="shrink-0 p-1.5 -m-1 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors" title="Retirer"><IcTrash size={13} /></button>
                                </div>
                                <div className="mt-2 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
                                  <div className="flex items-end gap-3">
                                    <div>
                                      <span className="block text-[10px] font-medium text-gray-400 mb-0.5">Qté</span>
                                      <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden bg-white">
                                        <button type="button" onClick={() => updateReserveLine(line.id, 'quantity', Math.max(1, qty - 1))} className="h-8 w-8 flex items-center justify-center bg-white text-gray-900 hover:bg-gray-100 transition-all" aria-label="Diminuer la quantité"><IcMinus size={9} /></button>
                                        <input
                                          type="number"
                                          min="1"
                                          value={line.quantity}
                                          onChange={(e) => updateReserveLine(line.id, 'quantity', e.target.value)}
                                          className="w-11 h-8 text-center text-sm font-semibold border-x border-gray-200 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                          aria-label="Quantité"
                                        />
                                        <button type="button" onClick={() => updateReserveLine(line.id, 'quantity', qty + 1)} className="h-8 w-8 flex items-center justify-center bg-white text-gray-900 hover:bg-gray-100 transition-all" aria-label="Augmenter la quantité"><IcPlus size={9} /></button>
                                      </div>
                                    </div>
                                    <div>
                                      <span className="block text-[10px] font-medium text-gray-400 mb-0.5">Avance</span>
                                      <div className="relative">
                                        <input
                                          type="number"
                                          step="0.001"
                                          min="0"
                                          value={line.deposit_amount}
                                          onChange={(e) => updateReserveLine(line.id, 'deposit_amount', e.target.value)}
                                          placeholder="0.000"
                                          className="w-28 h-8 pl-2.5 pr-11 border border-gray-200 rounded-lg text-sm font-semibold tabular-nums focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                                          aria-label="Avance"
                                        />
                                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 pointer-events-none">DT</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right ml-auto">
                                    <span className="block text-[10px] font-medium text-gray-400">Total ligne</span>
                                    <span className="text-sm font-bold text-gray-900 tabular-nums">{lineTotal.toFixed(3)} <span className="text-[10px] font-semibold text-gray-400">DT</span></span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {reserveLines.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-xl">Aucun produit sélectionné.</p>
                      )}
                    </div>
                  </div>

                  {/* expected arrival */}
                  <div className="order-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Arrivée prévue</label>
                    <input
                      type="date"
                      value={reserveForm.expected_arrival_date}
                      onChange={(e) => setReserveForm({ ...reserveForm, expected_arrival_date: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                    />
                  </div>

                  {/* note */}
                  <div className="order-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Note</label>
                    <textarea
                      rows="2"
                      value={reserveForm.note}
                      onChange={(e) => setReserveForm({ ...reserveForm, note: e.target.value })}
                      placeholder="Détails (variante, taille, commentaire)..."
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                    />
                  </div>
                </div>
                {/* Résumé de la commande — vertical stack (owner order:
                    Total réservation → Reste à payer → Total avance) */}
                {reserveLines.length > 0 && (
                  <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex flex-col gap-2">
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Résumé de la commande</span>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total réservation</span>
                      <span className="text-sm font-bold text-gray-900 tabular-nums">{reserveTotal.toFixed(3)} <span className="text-[10px] font-semibold text-gray-400">DT</span></span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-red-500 uppercase tracking-wide">Reste à payer</span>
                      <span className="text-lg font-extrabold text-red-600 tabular-nums leading-tight">{reserveReste.toFixed(3)} <span className="text-xs font-bold text-red-300">DT</span></span>
                    </div>
                    <div className="flex justify-between items-center bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <label htmlFor="reserve-avance-total" className="text-sm font-semibold text-red-700">Total avance</label>
                      <div className="relative">
                        <input
                          id="reserve-avance-total"
                          type="number"
                          min="0"
                          step="0.001"
                          value={avanceEditing ? avanceDraft : (reserveAvance > 0 ? r3(reserveAvance) : '')}
                          onFocus={() => { setAvanceEditing(true); setAvanceDraft(reserveAvance > 0 ? String(r3(reserveAvance)) : ''); }}
                          onChange={(e) => { setAvanceDraft(e.target.value); distributeAvance(e.target.value); }}
                          onBlur={() => setAvanceEditing(false)}
                          placeholder="0.000"
                          className="w-28 h-8 pl-2.5 pr-10 rounded-lg border border-red-200 bg-white text-lg font-bold text-red-700 tabular-nums text-right focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          aria-label="Total avance donné par le client"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-red-300 pointer-events-none">DT</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* footer: Retour (left) — Confirmer (right) */}
                <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center">
                  <button type="button" onClick={() => setReserveStep('select')} className="btn-g px-5 py-2.5 text-sm">Retour</button>
                  <button
                    type="submit"
                    disabled={reserveSubmitting || reserveLines.length === 0}
                    className="btn-p px-5 py-2.5 text-sm gap-2"
                  >
                    <IcClipboardList size={13} /> {reserveSubmitting ? 'Enregistrement...' : 'Confirmer la réservation'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* ----- "Nouveau produit" sub-popup (reserve an off-catalogue product) ----- */}
          {showNewProductForm && (
            <div className="modal-backdrop z-[60] flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowNewProductForm(false); }}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="modal-head px-5 py-4">
                  <h3 className="modal-title flex items-center gap-2"><IcPlus /> Nouveau produit</h3>
                  <button onClick={() => setShowNewProductForm(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Nom du produit <span className="text-red-600">*</span></label>
                    <input
                      ref={newProductNameRef}
                      type="text"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                      placeholder="Ex : Crème hydratante XYZ"
                      className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${newProductMatches.exact ? 'border-amber-300 ' : 'border-gray-200 '}`}
                    />
                    {/* Doublon exact : ce nom est déjà une fiche du catalogue */}
                    {newProductMatches.exact && (
                      <div role="alert" className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="flex items-center gap-1.5 text-xs font-bold text-amber-700">
                          <IcAlertTriangle size={11} /> Ce produit existe déjà au catalogue
                        </p>
                        <div className="mt-2 flex items-center gap-2.5 rounded-lg bg-white border border-amber-100 px-2.5 py-2">
                          <span className="h-9 w-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                            {newProductMatches.exact.image
                              ? <img src={newProductMatches.exact.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              : <IcPackage className="text-gray-300" size={14} />}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-semibold text-gray-800 truncate">{newProductMatches.exact.name}</span>
                            <span className="block text-[11px] text-gray-400 truncate">
                              {newProductMatches.exact.code ? `Code: ${newProductMatches.exact.code}` : 'Sans code'}
                            </span>
                          </span>
                          {Number(newProductMatches.exact.stock || 0) <= 0 ? (
                            <span className="bdg bdg-rupture shrink-0">Rupture</span>
                          ) : (
                            <span className="bdg bdg-stock-ok shrink-0">{Number(newProductMatches.exact.stock || 0)} en stock</span>
                          )}
                        </div>
                        {Number(newProductMatches.exact.stock || 0) > 0 && (
                          <p className="mt-1.5 text-[11px] text-amber-700">
                            En stock — vendez-le directement depuis le catalogue, pas besoin de le réserver.
                          </p>
                        )}
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {Number(newProductMatches.exact.stock || 0) <= 0 && (
                            <button
                              type="button"
                              onClick={() => pickExistingReserveProduct(newProductMatches.exact)}
                              className="btn-p py-2 text-xs"
                            >
                              Utiliser ce produit
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => { newProductNameRef.current?.focus(); newProductNameRef.current?.select(); }}
                            className={`btn-g-edit py-2 text-xs ${Number(newProductMatches.exact.stock || 0) > 0 ? 'col-span-2' : ''}`}
                          >
                            Modifier le nom
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Noms proches : peut-être une fiche existante mal orthographiée */}
                    {!newProductMatches.exact && newProductMatches.similar.length > 0 && (
                      <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
                        <p className="text-xs font-bold text-accent">Produits proches au catalogue — est-ce l'un d'eux ?</p>
                        <div className="mt-1.5 space-y-1">
                          {newProductMatches.similar.map((p) => (
                            <button
                              type="button"
                              key={`dup-${p.id}`}
                              onClick={() => pickExistingReserveProduct(p)}
                              className="w-full flex items-center gap-2.5 rounded-app bg-white border border-gray-200 hover:border-accent hover:bg-blue-50 px-2.5 py-1.5 text-left"
                              title="Utiliser ce produit du catalogue"
                            >
                              <span className="h-8 w-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                                {p.image
                                  ? <img src={p.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                  : <IcPackage className="text-gray-300" size={13} />}
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-[13px] font-medium text-gray-800 truncate">{p.name}</span>
                                <span className="block text-[10px] text-gray-400 truncate">{p.code ? `Code: ${p.code}` : 'Sans code'} · {Number(p.stock || 0)} en stock</span>
                              </span>
                              <span className="shrink-0 text-[11px] font-bold text-accent">Utiliser</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix d'achat estimé (DT) <span className="text-red-600">*</span></label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={newProduct.cost}
                      onChange={(e) => setNewProduct({ ...newProduct, cost: e.target.value })}
                      placeholder="0.000"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                    />
                  </div>
                  {/* Estimated selling price (read-only) */}
                  <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Prix de vente estimé</p>
                      <p className="text-[10px] text-gray-400">TVA {Math.round(TVA_RATE * 100)}% · marge {Math.round(DEFAULT_MARGIN_RATE * 100)}%</p>
                    </div>
                    <p className="text-lg font-extrabold text-red-600">{newProductSalePrice.toFixed(3)} <span className="text-xs font-semibold text-gray-400">DT</span></p>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end items-center gap-2">
                  <button type="button" onClick={() => setShowNewProductForm(false)} className="btn-g px-5 py-2.5 text-sm">Annuler</button>
                  {/* Réserver = primaire vert, comme « Réserver (n) » du pied de liste (Studio 2026-09-20) */}
                  <button type="button" onClick={addNewProductReserve} className="btn-p px-5 py-2.5 rounded-xl text-sm">
                    <IcClipboardList size={13} /> Réserver
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== Réserver un soin popup (creates a rendez-vous / session) ===== */}
      {showSoinModal && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowSoinModal(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-12 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="modal-head px-5 py-4 items-start">
              <div className="flex items-center gap-3">
                {soinStep === 'confirm' && (
                  <button onClick={() => setSoinStep('select')} className="h-9 w-9 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white flex items-center justify-center" title="Retour"><IcChevronLeft /></button>
                )}
                <div>
                  <h3 className="modal-title text-lg leading-tight flex items-center gap-2"><IcCalendar /> {soinStep === 'confirm' ? 'Confirmer la réservation' : 'Réserver un soin'}</h3>
                  <p className="text-xs text-gray-300">{soinStep === 'confirm' ? 'Note, paiement et confirmation' : 'Choisissez le soin, le client et la date'}</p>
                </div>
              </div>
              <button onClick={() => setShowSoinModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>

            {soinStep === 'select' ? (
              <>
                <div className="p-5 overflow-y-auto space-y-4">
                  {/* Soin */}
                  <div>
                    <label className="block text-xs font-medium text-gray-900 mb-1.5">Soin <span className="req">*</span></label>
                    <select
                      value={soinForm.service}
                      onChange={(e) => setSoinForm({ ...soinForm, service: e.target.value })}
                      className="inp w-full px-3 py-2.5 rounded-lg text-gray-900"
                    >
                      <option value="">Sélectionner un soin</option>
                      {soinServicesList.map((s) => (
                        <option key={s.id} value={s.id}>{s.name} — {(parseFloat(s.price) || 0).toFixed(3)} DT</option>
                      ))}
                    </select>
                    {soinServicesList.length === 0 && (
                      <p className="mt-1 text-xs text-gray-400">Aucun soin disponible dans le catalogue.</p>
                    )}
                  </div>

                  {/* Client */}
                  <div>
                    <label className="block text-xs font-medium text-gray-900 mb-1.5">Client <span className="req">*</span></label>
                    <div className="relative">
                      <IcUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={12} />
                      <input
                        type="text"
                        value={soinCustomer ? `${soinCustomer.first_name || ''} ${soinCustomer.last_name || ''}`.trim() : soinCustomerQuery}
                        onChange={(e) => { const v = e.target.value; setSoinCustomerQuery(v); if (soinCustomer) setSoinCustomer(null); searchSoinCustomers(v); setShowSoinCustomerDropdown(true); }}
                        onFocus={() => setShowSoinCustomerDropdown(true)}
                        placeholder="Rechercher un client (nom ou téléphone)..."
                        className="inp w-full pl-9 pr-9 py-2.5 rounded-lg text-gray-900"
                      />
                      {soinCustomer && (
                        <button type="button" onClick={() => { setSoinCustomer(null); setSoinCustomerQuery(''); setSoinCustomers([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><IcClose size={12} /></button>
                      )}
                      {/* Autocomplete client (Brand Kit §05) : liste collée sous
                          le champ, nom en gras + téléphone secondaire, survol vert */}
                      {showSoinCustomerDropdown && !soinCustomer && soinCustomers.length > 0 && (
                        <div className="ac-list mt-1 rounded-lg max-h-52 overflow-y-auto">
                          {soinCustomers.map((c) => (
                            <div key={c.id} onClick={() => { setSoinCustomer(c); setShowSoinCustomerDropdown(false); }} className="ac-row px-3 py-2">
                              <span className="ac-name text-sm truncate">{c.first_name} {c.last_name || ''}</span>
                              {c.phone && <span className="ac-sub text-xs shrink-0">{c.phone}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Calendar — pick a day (scroll months) */}
                  <div>
                    <label className="block text-xs font-medium text-gray-900 mb-1.5">Date du rendez-vous <span className="req">*</span></label>
                    {(() => {
                      const { year, month } = soinCalMonth;
                      const monthLabel = new Date(year, month, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                      const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0
                      const daysInMonth = new Date(year, month + 1, 0).getDate();
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      const canPrev = new Date(year, month, 1) > new Date(today.getFullYear(), today.getMonth(), 1);
                      const cells = [];
                      for (let i = 0; i < firstWeekday; i++) cells.push(null);
                      for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                      return (
                        <div className="border border-gray-200 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-2">
                            {/* Boutons-2 §7 : Précédent / Suivant icône seule = btn-s carré 36px */}
                            <button type="button" disabled={!canPrev} onClick={() => shiftSoinMonth(-1)} title="Précédent" className="btn-s h-8 w-8"><IcChevronLeft size={12} /></button>
                            <span className="text-sm font-semibold text-gray-800 capitalize">{monthLabel}</span>
                            <button type="button" onClick={() => shiftSoinMonth(1)} title="Suivant" className="btn-s h-8 w-8"><IcChevronRight size={12} /></button>
                          </div>
                          {/* Calendrier (Brand Kit §05) : en-tête 10.5px gray-400, gap 3 ;
                              cases bordées #E5E7EB rayon 5 padding 4 10.5px min 34px ;
                              jour choisi = bordé 2px encre 700 ; passé gray-300 */}
                          <div className="grid grid-cols-7 gap-1 mb-1">
                            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
                              <div key={d} className="text-center text-[10px] text-gray-400 py-1">{d}</div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {cells.map((d, i) => {
                              if (d === null) return <div key={`e${i}`} />;
                              const dateObj = new Date(year, month, d);
                              const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                              const past = dateObj < today;
                              const selected = dayStr === soinDay;
                              return (
                                <button
                                  key={dayStr}
                                  type="button"
                                  disabled={past}
                                  onClick={() => { setSoinDay(dayStr); setSoinPeriod(''); }}
                                  className={`h-9 rounded-lg text-sm flex items-center justify-center transition-colors ${selected ? 'border-2 border-gray-900 bg-white text-gray-900 font-bold' : past ? 'border border-gray-200 text-gray-300 cursor-not-allowed' : 'border border-gray-200 text-gray-900 hover:bg-gray-100'}`}
                                >{d}</button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Matin / Après-midi (after a day is chosen) */}
                  {soinDay && (
                    <div className="grid grid-cols-2 gap-2">
                      {['matin', 'apresmidi'].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setSoinPeriod(p)}
                          className={`py-2.5 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${soinPeriod === p ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        >
                          {p === 'matin' ? <IcSun size={13} /> : <IcMoon size={13} />} {PERIOD_LABEL[p]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Pied de modale (Brand Kit §05) : 11/16px, filet gray-100, gap 8 */}
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end items-center gap-2">
                  <button type="button" onClick={() => setShowSoinModal(false)} className="btn-g px-5 py-2.5 text-sm">Annuler</button>
                  {/* Boutons-2 §2 : Réserver = secondaire (blanc bordé encre) */}
                  <button type="button" onClick={goToSoinConfirm} className="btn-s px-5 py-2.5 rounded-xl text-sm">
                    <IcCalendar size={13} /> Réserver le soin
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleCreateSoinReservation} className="flex flex-col overflow-hidden">
                <div className="p-5 overflow-y-auto space-y-4">
                  {/* Recap */}
                  {(() => {
                    const soin = soinServicesList.find((s) => s.id === parseInt(soinForm.service, 10));
                    const sp = parseFloat(soin?.price) || 0;
                    const sr = parseFloat(soin?.remise) || 0;
                    const finalPrice = sr > 0 ? sp * (1 - sr / 100) : sp;
                    const sched = soinScheduledTime();
                    const dateLabel = sched ? new Date(sched).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : '';
                    return (
                      <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 space-y-1.5">
                        <div className="flex justify-between text-sm gap-3"><span className="text-gray-500">Soin</span><span className="font-semibold text-gray-900 text-right">{soin?.name || '—'}</span></div>
                        <div className="flex justify-between text-sm gap-3"><span className="text-gray-500">Client</span><span className="font-semibold text-gray-900 text-right">{soinCustomer ? `${soinCustomer.first_name || ''} ${soinCustomer.last_name || ''}`.trim() : '—'}</span></div>
                        <div className="flex justify-between text-sm gap-3"><span className="text-gray-500">Rendez-vous</span><span className="font-semibold text-gray-900 text-right capitalize">{dateLabel} · {PERIOD_LABEL[soinPeriod]}</span></div>
                        <div className="flex justify-between text-sm gap-3"><span className="text-gray-500">Prix</span><span className="font-bold text-rose-700">{finalPrice.toFixed(3)} DT</span></div>
                      </div>
                    );
                  })()}

                  {/* Note */}
                  <div>
                    <label className="block text-xs font-medium text-gray-900 mb-1.5">Note</label>
                    <textarea
                      rows="2"
                      value={soinForm.notes}
                      onChange={(e) => setSoinForm({ ...soinForm, notes: e.target.value })}
                      placeholder="Détails du rendez-vous..."
                      className="inp w-full px-3 py-2.5 rounded-lg text-gray-900"
                    />
                  </div>

                  {/* Payment choice */}
                  <div>
                    <label className="block text-xs font-medium text-gray-900 mb-1.5">Paiement</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setSoinPayment('paid')} className={`py-2.5 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${soinPayment === 'paid' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        <IcBanknote size={13} /> Payer maintenant
                      </button>
                      <button type="button" onClick={() => setSoinPayment('unpaid')} className={`py-2.5 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${soinPayment === 'unpaid' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        <IcHistory size={13} /> Payer plus tard
                      </button>
                    </div>
                  </div>

                  {/* Payer avec points — coût en points défini dans Gestion des Catégories Client (pas 1 pt = 1 DT) */}
                  {soinCustomer && soinCustomer.id !== 5 && (() => {
                    const soin = soinServicesList.find((s) => s.id === parseInt(soinForm.service, 10));
                    const avail = round3(parseFloat(soinCustomer.points) || 0);
                    const cost = soinPointsCost(categoryById(soinCustomer.category), soin);
                    if (cost === null) return null; // ce soin n'a pas de prix en points pour cette catégorie
                    const canPay = avail >= cost;
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-medium text-gray-600 flex items-center gap-1"><IcGift size={11} className="text-green-600" /> Payer avec points</label>
                          <span className="text-[11px] text-gray-500">Dispo: <span className="font-semibold text-green-700">{Math.floor(avail)}</span></span>
                        </div>
                        <button
                          type="button"
                          disabled={!canPay && !soinPayWithPoints}
                          onClick={() => setSoinPayWithPoints((v) => !v)}
                          className={`w-full py-2.5 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${soinPayWithPoints ? 'border-green-500 bg-green-600 text-white' : canPay ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
                        >
                          <IcGift size={13} /> {soinPayWithPoints ? `Payé avec ${cost} points` : canPay ? `Payer avec points (${cost} pts)` : 'Points insuffisants'}
                        </button>
                      </div>
                    );
                  })()}
                </div>
                <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center gap-2">
                  <button type="button" onClick={() => setSoinStep('select')} className="btn-g px-5 py-2.5 text-sm">Retour</button>
                  <button type="submit" disabled={soinSubmitting} className="btn-p px-5 py-2.5 text-sm gap-2">
                    <IcCheck size={13} /> {soinSubmitting ? 'Réservation...' : 'Confirmer la réservation'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="modal-backdrop flex items-center justify-center z-50" onClick={(e) => {
          // Close dialog when clicking outside
          if (e.target === e.currentTarget) {
            setShowAddCustomerModal(false);
          }
        }}>
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="modal-head -mx-6 -mt-6 mb-4 rounded-t-lg">
              <h3 className="modal-title text-lg">Ajouter un nouveau client</h3>
              <button
                onClick={() => setShowAddCustomerModal(false)}
                className="text-gray-400 hover:text-white p-1"
                aria-label="Fermer"
              >
                <IcClose size={18} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                <input
                  type="text"
                  value={newCustomerData.first_name}
                  onChange={(e) => setNewCustomerData({...newCustomerData, first_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="Prénom du client"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={newCustomerData.last_name}
                  onChange={(e) => setNewCustomerData({...newCustomerData, last_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="Nom du client"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={newCustomerData.phone}
                  onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="Numéro de téléphone"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newCustomerData.email}
                  onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="Email du client"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input
                  type="text"
                  value={newCustomerData.address}
                  onChange={(e) => setNewCustomerData({...newCustomerData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="Adresse du client"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newCustomerData.points}
                  onChange={(e) => setNewCustomerData({...newCustomerData, points: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Score</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newCustomerData.score}
                  onChange={(e) => setNewCustomerData({...newCustomerData, score: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Crédit</label>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={newCustomerData.credit}
                  onChange={(e) => setNewCustomerData({...newCustomerData, credit: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Crédit maximum</label>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={newCustomerData.credit_maximum}
                  onChange={(e) => setNewCustomerData({...newCustomerData, credit_maximum: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="0"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowAddCustomerModal(false)}
                className="btn-g flex-1 px-4 py-2"
              >
                Annuler
              </button>
              <button
                onClick={handleAddCustomer}
                className="btn-add-pop flex-1 px-4 py-2"
              >
                Ajouter Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Upgrade Modal - Red & Black Theme */}
      {showUpgradeModal && upgradeInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-4 animate-fadeIn overflow-y-auto">
          <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-3xl shadow-2xl w-full max-w-xl my-8 transform animate-scaleIn relative border-2 border-red-600 overflow-hidden">
            
            {/* Animated Red Glow Effect */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent animate-pulse"></div>
            </div>

            {/* Animated Particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-10 left-10 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></div>
              <div className="absolute top-20 right-20 w-1.5 h-1.5 bg-gray-400 rounded-full animate-ping opacity-60" style={{animationDelay: '0.5s'}}></div>
              <div className="absolute bottom-20 left-20 w-1 h-1 bg-red-400 rounded-full animate-ping opacity-50" style={{animationDelay: '1s'}}></div>
              <div className="absolute bottom-32 right-16 w-2 h-2 bg-gray-500 rounded-full animate-ping opacity-40" style={{animationDelay: '1.5s'}}></div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowUpgradeModal(false);
                setUpgradeInfo(null);
                Swal.fire({
                  title: 'Vente enregistrée!',
                  text: 'La vente a été créée avec succès!',
                  icon: 'success',
                  confirmButtonColor: KI.coralDark,
                  timer: 2000
                });
              }}
              className="absolute top-4 right-4 z-50 text-gray-400 hover:text-white rounded-full p-2 hover:bg-white/10 transition-all"
            >
              <IcClose size={18} />
            </button>

            {/* Content */}
            <div className="relative z-10 p-6 sm:p-8">
              
              {/* Logo with Animation */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-red-600 rounded-full blur-xl opacity-50 animate-pulse"></div>
                  <img 
                    src={require('../../assets/icon.png')} 
                    alt="Ki-Store" 
                    className="w-20 h-20 sm:w-24 sm:h-24 relative z-10 drop-shadow-2xl animate-bounce"
                  />
                </div>
              </div>

              {/* Title */}
              <div className="text-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-2 tracking-tight">
                  FÉLICITATIONS!
                </h2>
                <div className="h-1 w-24 bg-gradient-to-r from-red-600 to-red-400 mx-auto rounded-full"></div>
              </div>

              {/* Customer Name */}
              <p className="text-center text-gray-300 mb-6 text-base sm:text-lg">
                <span className="font-bold text-red-500">{upgradeInfo.customerName}</span>
                <span className="text-gray-400"> a été promu!</span>
              </p>

              {/* Category Upgrade */}
              <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-4 sm:p-6 mb-6 border border-gray-700">
                <div className="flex items-center justify-center gap-4">
                  {/* Old Category */}
                  <div className="text-center flex-1">
                    <div className="text-xs text-gray-500 mb-2">Ancienne</div>
                    <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                      <div className="text-sm sm:text-base font-bold text-gray-300">{upgradeInfo.oldCategory.name}</div>
                      <div className="text-xs text-gray-400 mt-1">{upgradeInfo.oldCategory.remise}%</div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="text-red-500 text-2xl sm:text-3xl font-bold animate-pulse">→</div>

                  {/* New Category */}
                  <div className="text-center flex-1">
                    <div className="text-xs text-red-400 mb-2">Nouvelle</div>
                    <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-lg p-3 border-2 border-red-500 shadow-lg shadow-red-900/50">
                      <div className="text-sm sm:text-base font-bold text-white">{upgradeInfo.newCategory.name}</div>
                      <div className="text-xs text-red-100 mt-1">{upgradeInfo.newCategory.remise}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Points & Info Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {/* Points Gained */}
                <div className="bg-gray-800 bg-opacity-50 rounded-xl p-4 border border-gray-700 text-center">
                  <div className="text-xs text-gray-400 mb-1">Points gagnés</div>
                  <div className="text-2xl font-bold text-red-500">+{upgradeInfo.pointsGained.toFixed(0)}</div>
                </div>

                {/* Cashback Rate */}
                <div className="bg-gray-800 bg-opacity-50 rounded-xl p-4 border border-gray-700 text-center">
                  <div className="text-xs text-gray-400 mb-1">Points</div>
                  <div className="text-2xl font-bold text-red-500">{upgradeInfo.newCategory.points_value}%</div>
                </div>
              </div>

              {/* Features */}
              {upgradeInfo.newCategory.features && upgradeInfo.newCategory.features.length > 0 && (
                <div className="bg-gray-800 bg-opacity-30 rounded-xl p-4 mb-6 border border-gray-700">
                  <div className="flex items-center mb-3">
                    <IcGift className="text-red-500 mr-2" />
                    <h3 className="font-bold text-white text-sm">Avantages débloqués</h3>
                  </div>
                  <ul className="space-y-2">
                    {upgradeInfo.newCategory.features.map((feature, index) => (
                      <li key={index} className="flex items-center justify-between bg-gray-700 bg-opacity-50 p-2 rounded-lg text-sm animate-slideIn" style={{animationDelay: `${index * 0.1}s`}}>
                        <div className="flex items-center flex-1">
                          <span className="text-red-500 mr-2 flex-shrink-0">✓</span>
                          <span className="text-gray-300">{typeof feature === 'string' ? feature : feature.feature}</span>
                        </div>
                        {typeof feature === 'object' && feature.score !== undefined && (
                          <span className="bdg bdg-score rounded-full text-xs px-2.5 py-[3px] ml-2">
                            {feature.score} pts
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Button */}
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  setUpgradeInfo(null);
                  Swal.fire({
                    title: 'Vente enregistrée!',
                    text: 'La vente a été créée avec succès!',
                    icon: 'success',
                    confirmButtonColor: KI.coralDark,
                    timer: 2000
                  });
                }}
                className="btn-p w-full py-4 px-6 rounded-xl transform hover:scale-105 transition-all uppercase tracking-wide"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rewards Bubble - Floating on right */}
      {/* Rewards Detail Dialog */}
      {showRewardsDialog && rewardsSummary && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* Header — Composants-2 §10 : aplat encre */}
            <div className="modal-head px-6 py-4 items-start">
              <div className="flex items-center gap-3">
                <IcAward className="text-2xl" />
                <div>
                  <h2 className="modal-title text-xl">{rewardsSummary.customer_name}</h2>
                  <p className="text-sm text-gray-300">{rewardsSummary.category?.name || 'Sans catégorie'}</p>
                </div>
              </div>
              <button
                onClick={() => setShowRewardsDialog(false)}
                className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
                aria-label="Fermer"
              >
                <IcClose size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-5 text-center border-2 border-yellow-300 shadow-sm">
                  <div className="text-3xl font-bold text-yellow-600 mb-1">{rewardsSummary.points?.toFixed(2) || 0}</div>
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Points</div>
                  <div className="text-xs text-gray-500 mt-1">Disponibles pour récompenses</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 text-center border-2 border-blue-300 shadow-sm">
                  <div className="text-3xl font-bold text-blue-600 mb-1">{rewardsSummary.category?.features?.length || 0}</div>
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Récompenses</div>
                  <div className="text-xs text-gray-500 mt-1">Disponibles</div>
                </div>
              </div>

              {/* Category Features with Progress & Claim */}
              {rewardsSummary.category && rewardsSummary.category.features && (
                <div className="mb-6">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center text-lg">
                    <IcGift className="mr-2 text-red-500" />
                    Récompenses Disponibles
                  </h3>
                  <div className="space-y-3">
                    {rewardsSummary.category.features.map((feature, idx) => {
                      const currentCashback = rewardsSummary.points || 0;
                      const requiredPoints = feature.points || 0;
                      const canUse = currentCashback >= requiredPoints;
                      const progress = requiredPoints > 0 ? Math.min((currentCashback / requiredPoints) * 100, 100) : 100;
                      const timesCanUse = Math.floor(currentCashback / requiredPoints);
                      
                      return (
                        <div
                          key={idx}
                          className={`p-4 rounded-xl border-2 transition-all shadow-sm ${
                            canUse
                              ? 'bg-gradient-to-br from-green-50 via-yellow-50 to-green-50 border-green-400 hover:shadow-md'
                              : 'bg-white border-gray-300'
                          }`}
                        >
                          {/* Feature Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-2xl ${canUse ? 'text-green-600' : 'text-gray-400'}`}>
                                  {canUse ? <IcGift /> : <IcLock />}
                                </span>
                                <span className={`text-base font-bold ${canUse ? 'text-gray-900' : 'text-gray-500'}`}>
                                  {typeof feature === 'string' ? feature : feature.feature}
                                </span>
                              </div>
                              {canUse && timesCanUse > 1 && (
                                <span className="text-xs text-green-600 font-semibold ml-9 flex items-center gap-1">
                                  <IcCheckCircle size={10} />
                                  Utilisable {timesCanUse} fois
                                </span>
                              )}
                            </div>
                            {typeof feature === 'object' && feature.points !== undefined && (
                              <span className={`bdg whitespace-nowrap ml-2 ${canUse ? 'bdg-done' : 'bdg-neutral'}`}>
                                {feature.points} pts
                              </span>
                            )}
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mb-3">
                            <div className="flex justify-between text-xs mb-2">
                              <span className={canUse ? 'text-green-600 font-bold' : 'text-gray-600 font-semibold'}>
                                Vous avez: {currentCashback.toFixed(2)} pts
                              </span>
                              <span className="text-gray-500 font-medium">Requis: {requiredPoints} pts</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                              <div
                                className={`h-4 rounded-full transition-all duration-500 ${
                                  canUse 
                                    ? 'bg-gradient-to-r from-green-400 via-green-500 to-green-600' 
                                    : 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500'
                                }`}
                                style={{ width: `${progress}%` }}
                              >
                                <div className="h-full flex items-center justify-center">
                                  <span className="text-[10px] font-bold text-white drop-shadow">
                                    {progress.toFixed(0)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            {!canUse && (
                              <p className="text-xs text-orange-600 font-semibold mt-2 flex items-center gap-1">
                                <IcAlertTriangle size={12} />
                                Encore {(requiredPoints - currentCashback).toFixed(2)} pts nécessaires
                              </p>
                            )}
                          </div>
                          
                          {/* Use Button */}
                          <button
                            onClick={() => handleClaimReward(feature)}
                            disabled={!canUse}
                            className={`w-full py-3 px-4 transition-all transform flex items-center justify-center gap-2 ${
                              canUse
                                ? 'btn-p hover:scale-105'
                                : 'btn-p'
                            }`}
                          >
                            {canUse ? (
                              <>
                                <IcGift className="text-lg" />
                                Utiliser Maintenant ({timesCanUse}x disponible)
                              </>
                            ) : (
                              <>
                                <IcLock />
                                Points insuffisants
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


              {rewardsSummary.total_rewards === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <IcGift className="mx-auto text-4xl mb-2 opacity-30" />
                  <p>Aucune récompense pour le moment</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Use Reward Confirmation Dialog */}
      {useRewardDialogOpen && rewardToUse && (
        <div className="modal-backdrop flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="rounded-full bg-yellow-100 p-4">
                  <IcGift className="text-yellow-600 text-3xl" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                Utiliser cette récompense?
              </h3>
              <p className="text-gray-600 text-center mb-4">
                <span className="font-semibold text-gray-800">{rewardToUse.reward}</span>
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Coût:</span>
                  <span className="font-bold text-yellow-600">{rewardToUse.points_cost} pts</span>
                </div>
                {rewardsSummary && (
                  <div className="flex justify-between items-center text-sm mt-2 pt-2 border-t border-yellow-200">
                    <span className="text-gray-600">Après utilisation:</span>
                    <span className="font-semibold text-gray-800">
                      {(rewardsSummary.points - rewardToUse.points_cost).toFixed(2)} pts
                    </span>
                  </div>
                )}
              </div>
              <p className="text-xs text-red-600 text-center mb-6">Cette action est irréversible.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setUseRewardDialogOpen(false);
                    setRewardToUse(null);
                  }}
                  className="btn-g flex-1 px-4 py-3 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleUseReward}
                  className="btn-p flex-1 px-4 py-3 transition"
                >
                  Utiliser
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reward Earned Modal */}
      {showRewardEarnedModal && earnedReward && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-4 animate-fadeIn">
          <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-3xl shadow-2xl w-full max-w-lg transform animate-scaleIn relative border-2 border-yellow-500">
            {/* Animated Glow */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent animate-pulse"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 p-8 text-center">
              {/* Trophy Icon */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-yellow-500 rounded-full blur-xl opacity-50 animate-pulse"></div>
                  <IcTrophy className="w-20 h-20 text-yellow-400 relative z-10 drop-shadow-2xl animate-bounce" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-3xl font-black text-white mb-2">
                FÉLICITATIONS!
              </h2>
              <div className="h-1 w-32 bg-gradient-to-r from-yellow-500 to-yellow-300 mx-auto rounded-full mb-6"></div>

              {/* Reward Description */}
              <div className="bg-gray-800 bg-opacity-50 rounded-2xl p-6 mb-6 border border-yellow-600">
                <div className="text-yellow-400 text-sm mb-2">Nouvelle récompense disponible</div>
                <div className="text-white text-xl font-bold">{earnedReward.feature}</div>
                <div className="text-gray-400 text-sm mt-2">Vous pouvez maintenant réclamer cette récompense!</div>
                <div className="text-yellow-300 text-sm mt-1 font-semibold">Coût: {earnedReward.points} points</div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={async () => {
                    // Load customer's rewards and show dialog
                    if (earnedRewardCustomerId) {
                      try {
                        const summary = await rewardService.getCustomerSummary(earnedRewardCustomerId);
                        setRewardsSummary(summary);
                        setShowRewardEarnedModal(false);
                        setEarnedReward(null);
                        setEarnedRewardCustomerId(null);
                        setShowRewardsDialog(true);
                        
                        Swal.fire({
                          title: 'Vente enregistrée!',
                          text: 'La vente a été créée avec succès!',
                          icon: 'success',
                          confirmButtonColor: KI.coralDark,
                          timer: 2000,
                          toast: true,
                          position: 'top-end',
                          showConfirmButton: false
                        });
                      } catch (err) {
                        console.error('Error loading rewards:', err);
                        setShowRewardEarnedModal(false);
                        setEarnedReward(null);
                        setEarnedRewardCustomerId(null);
                      }
                    }
                  }}
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-4 px-6 rounded-xl shadow-lg transform hover:scale-105 transition-all uppercase tracking-wide flex items-center justify-center gap-2"
                >
                  <IcGift className="text-lg" />
                  Réclamer maintenant
                </button>
                
                <button
                  onClick={() => {
                    setShowRewardEarnedModal(false);
                    setEarnedReward(null);
                    setEarnedRewardCustomerId(null);
                    Swal.fire({
                      title: 'Vente enregistrée!',
                      text: 'La vente a été créée avec succès!',
                      icon: 'success',
                      confirmButtonColor: KI.coralDark,
                      timer: 2000
                    });
                  }}
                  className="btn-g w-full py-3 px-6 rounded-xl transform hover:scale-105 transition-all"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reward Confirmation Dialog */}
      {confirmRewardDialog && rewardToConfirm && rewardsSummary && (
        <div className="modal-backdrop flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header — Composants-2 §10 : aplat encre */}
            <div className="modal-head px-6 py-4">
              <h3 className="modal-title text-xl flex items-center gap-2">
                <IcGift className="text-2xl" />
                Utiliser cette récompense?
              </h3>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="bg-gradient-to-br from-green-50 to-yellow-50 rounded-lg p-5 mb-6 border-2 border-green-200">
                <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                  <span className="text-2xl text-green-600"><IcGift /></span>
                  {rewardToConfirm.feature}
                </h4>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-green-200">
                    <span className="text-gray-600">Coût:</span>
                    <span className="font-bold text-red-600 text-base">{rewardToConfirm.points} points</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-green-200">
                    <span className="text-gray-600">Solde actuel:</span>
                    <span className="font-bold text-yellow-600 text-base">{rewardsSummary.points.toFixed(2)} points</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 bg-white rounded-lg px-3">
                    <span className="text-gray-700 font-semibold">Nouveau solde:</span>
                    <span className="font-bold text-green-600 text-lg">
                      {(rewardsSummary.points - rewardToConfirm.points).toFixed(2)} points
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setConfirmRewardDialog(false);
                    setRewardToConfirm(null);
                  }}
                  className="btn-g flex-1 py-3 px-4 transition-all"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmClaimReward}
                  className="btn-p flex-1 py-3 px-4 transition-all gap-2"
                >
                  <IcCheckCircle />
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SellerSalesPage;
